// 전투 HUD(DOM). 콘셉트 ui-battle-hud 배치: 좌상단 초상·HP/MP·목표 패널, 우상단 스테이지·타이머 패널·일시정지·미니맵 링(+카메라 줌 버튼),
// 우측 콤보, 상단 중앙 보스 HP, 하단 중앙 각성 게이지. 틀·패널·초상은 GPT Image로 만든 원화(public/assets/ui/hud)이고,
// 이미지를 못 받으면 각 부품이 이전 CSS 모양으로 대체된다. 값이 바뀔 때만 DOM을 건드린다.
// 키보드(터치가 아닌 기기)는 하단 가운데 스킬 바(skillbar.ts)를 두고, 각성 게이지를 그 바로 위로 옮긴다.
// 체력 30% 이하에서는 화면 가장자리가 붉게 천천히 맥박친다(섬광이 아니다: 밝기 급변 없음, 1초에 1회 미만).
// 바 번쩍임·글자 효과는 COMBAT-FEEL 5.1 계약을 따른다: 번쩍임은 화면 하나의 리미터(hudFlash.ts)를 거치고, 효과 Animation은 생성자에서 한 번 만든다.
import { cameraResetKeyLabel, keymap } from '../shared/keymap';
import { PRAISE_TIERS } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import { Cell } from '../shared/levels';
import { AwakenPhase, BossState, MobState, SKILL_BTN_ALL, Scene, combatScene, type GameCommand, type GameView, type InputSink, type PlayableHeroId } from '../shared/types';
import { C, FONT, FONT_NUM, GOLD_TEXT, TEXT_OUTLINE, el, fmtNum, fmtTime, hudImg, hudUrl, portraitImage } from './dom';
import { goldText } from './rpgModel';
import { rebirthLook, rebirthStars } from '../shared/rebirth';
import { HUD_BAR_BOSS, HUD_BAR_HERO, HUD_BAR_TARGET, HUD_FLASH_MS, HudFlashGate } from './hudFlash';
import { RESERVE_BOSS, RESERVE_TARGET, type ScreenReserve } from './screenReserve';
import { elementRect, setPanelRect } from '../shared/panelRects';
import { ROOM_TIP_MS, RoomTipGate, mobNamesFor } from './regionModel';
import { SKILL_ICONS, SkillBar } from './skillbar';
import { LinkHud } from './linkHud';
import { RampageHud } from './rampageHud';
import { LinkHint } from './linkHint';
import { raidLayoutRev } from './raidHudLayout';
import { TargetFrameModel, type TargetFrameOut } from './targetFrame';
import { growthOf, levelUpParts, treeHeader } from './skillTreeModel';
import { skillLearnedParts, slotsOf } from './slotsModel';
import { xpBandBadgeText, xpBandHelpText, xpBandLevelUpNote } from './xpBandModel';
import { SkillBandQueue } from './skillBandQueue';
import { mountSkillArt, type SkillArt } from './skillArt';
import { LYRA_ICONS } from './lyraIcons';
import { viewH, viewRect, viewW, type ViewRect } from '../shared/viewport';
import { comboHudLayout, objectiveCompact, objectiveHudLayout, roomTipLayout, type HudRect } from './skillHudLayout';
import { hudMenuLayout } from './hudMenuLayout';
import { MENU_EVENT, MenuBar } from './menuBar';
import { assetUrl } from '../pwa/assetMap';
import { ELIXIRS, ELIXIR_MAX_SEC } from '../shared/content/consumables';
import { BOSS_DEFS, BOSS_KEYS } from '../shared/content/bosses';
import { RAGE_BAND_MS, RAGE_TEXT, rageMark } from '../game/bossRage';

const PANEL = 'background:linear-gradient(180deg,rgba(26,34,56,.94),rgba(16,22,42,.94));border:2px solid ' + C.gold + ';border-radius:10px;box-shadow:0 3px 10px rgba(0,0,0,.45);';
/** 비약 표지 테두리 색(ELIXIRS 순서: 힘·철벽·행운·지혜) */
const ELIXIR_TINT: readonly string[] = ['#ff7a5a', '#9fb8e6', '#5ae08a', '#c69aff'];
/** 남은 시간 문자열 표(0..60분, mm:ss) — 초마다 새 문자열을 만들지 않는다 */
const TIME_TEXT: readonly string[] = Array.from({ length: ELIXIR_MAX_SEC + 1 }, (_, s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'));
const MM = 150; // 미니맵 캔버스 해상도(px)
const MM_R = 24; // 미니맵 반경(셀)
/** 체력 경고 문턱(비율)과 위급 문턱 */
const LOW_HP = 0.3;
const CRIT_HP = 0.15;
const HUD_STYLE_ID = 'dh-hud-style';
const HUD_CSS = '@keyframes dhLowHp{0%,100%{opacity:.25}50%{opacity:.65}}';
const LOW_ANIM = 'dhLowHp 1.4s ease-in-out infinite';
const CRIT_ANIM = 'dhLowHp 0.95s ease-in-out infinite';
/** 콤보 흐려짐 불투명도 문자열 표(0..20) */
const OPACITY: readonly string[] = Array.from({ length: 21 }, (_, i) => String(i / 20));
/** 자세 바 채움: 평소 금색(보스 경직 바와 같은 스타일), 가드 브레이크 표시 중 진홍(화면 강조색 1개) */
const POSTURE_GOLD = 'linear-gradient(90deg,#c88a1a,#ffe07a)';
const POSTURE_BREAK = 'linear-gradient(90deg,#b3101f,#ff2a3d)';
const GB_SCALE = 1.35;
const GB_LABEL = 'transform-origin:100% 50%;position:absolute;white-space:nowrap;opacity:0;pointer-events:none;font:900 13px ' + FONT + ';letter-spacing:2px;color:' + C.crimson + ';' + TEXT_OUTLINE;
/** 대상 체력·자세는 전투 중앙 위 112px 띠 안에 둔다. 작은 화면도 최소 24px 위 여백. */
const TARGET_TOP = 32;
const TARGET_TOP_MIN = 24;
/** 보스 바 위 여백(px, 배율 없음) */
const BOSS_TOP = 20;
/** 보스 경직 바 폭과 그 오른쪽 '가드 브레이크' 글자 간격(디자인 px). CSS와 예약 영역 계산이 같은 값을 쓴다 */
const STAGGER_W = 420, BOSS_GB_GAP = 10;
/** 대상 자세 바 폭과 그 오른쪽 '가드 브레이크' 글자 간격(디자인 px) */
const POSTURE_W = 280, TARGET_GB_GAP = 16;
/** 예약 영역 아래 여유(디자인 px): 가드 브레이크 글자 판이 박스 아래로 조금 내려온다 */
const RESERVE_PAD = 4;
// 최초 표시·무적 태그 전환은 ResizeObserver보다 먼저 온다. 현재 HUD의 바·글꼴·여백을
// 기준으로 잡은 보수적 초기 크기다. 관측 후에는 실제 박스 크기로 대체한다.
const BOSS_BAR_W = 460, TARGET_BAR_W = 300;
const BOSS_RESERVE_H = 110, TARGET_RESERVE_H = 80;
const GB_RESERVE_W = 160, GB_RESERVE_H = 32;
const POSTURE_H = 6, POSTURE_BORDER = 1;
const BOSS_GB_TOP = -6, TARGET_GB_TOP = -9, TARGET_PAD_BOTTOM = 5;
/** ResizeObserver로 받는 박스 번호 */
const OBS_BOSS = 0, OBS_TARGET = 1, OBS_BOSS_GB = 2, OBS_TARGET_GB = 3;

// ── HUD 효과 키프레임(모듈 상수: 생성자에서 한 번 Animation에 넣는다. COMBAT-FEEL 5.1) ──
/** 바 번쩍임(결투장 측정: 맞은 쪽 체력 바가 67ms 하얗게): 67ms 밝게 유지 → 100ms에 꺼짐. 섬광 약화는 회색 톤의 약한 밝기 */
const FLASH_FULL: Keyframe[] = [{ filter: 'brightness(2.4) saturate(.25)' }, { filter: 'brightness(2.4) saturate(.25)', offset: 0.67 }, { filter: 'none' }];
const FLASH_CALM: Keyframe[] = [{ filter: 'brightness(1.35) saturate(.6)' }, { filter: 'brightness(1.35) saturate(.6)', offset: 0.67 }, { filter: 'none' }];
/** 가드 브레이크 글자 0.8초: 크게 나타나 자리 잡고 사라진다. 동작 줄이기는 불투명도만 */
const GB_MOVE: Keyframe[] = [
  { opacity: 1, transform: 'scale(' + GB_SCALE + ')', offset: 0 },
  { opacity: 1, transform: 'scale(1)', offset: 0.15 },
  { opacity: 1, transform: 'scale(1)', offset: 0.75 },
  { opacity: 0, transform: 'scale(1)', offset: 1 },
];
const GB_CALM: Keyframe[] = [{ opacity: 1, offset: 0 }, { opacity: 1, offset: 0.75 }, { opacity: 0, offset: 1 }];
/** 저스트 회피 글자 0.6초 */
const JUST_MOVE: Keyframe[] = [
  { opacity: 0, transform: 'scale(.7)', offset: 0 },
  { opacity: 1, transform: 'scale(1.12)', offset: 0.18 },
  { opacity: 1, transform: 'scale(1)', offset: 0.65 },
  { opacity: 0, transform: 'scale(1.04)', offset: 1 },
];
const JUST_CALM: Keyframe[] = [{ opacity: 0, offset: 0 }, { opacity: 1, offset: 0.18 }, { opacity: 1, offset: 0.65 }, { opacity: 0, offset: 1 }];
/** 방 정리 회복 '+N' 0.9초 */
const HEAL_MOVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(8px)', offset: 0 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.15 },
  { opacity: 1, transform: 'translateY(-6px)', offset: 0.7 },
  { opacity: 0, transform: 'translateY(-14px)', offset: 1 },
];
const HEAL_CALM: Keyframe[] = [{ opacity: 0, offset: 0 }, { opacity: 1, offset: 0.15 }, { opacity: 1, offset: 0.7 }, { opacity: 0, offset: 1 }];

/** EXP '+N' 0.9초: 막대 곁에서 살짝 떠올랐다 사라진다 */
const XPTICK_MOVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(6px)', offset: 0 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.15 },
  { opacity: 1, transform: 'translateY(-3px)', offset: 0.7 },
  { opacity: 0, transform: 'translateY(-8px)', offset: 1 },
];
const XPTICK_CALM: Keyframe[] = [{ opacity: 0, offset: 0 }, { opacity: 1, offset: 0.15 }, { opacity: 1, offset: 0.7 }, { opacity: 0, offset: 1 }];
/** 레벨업 띠 2.2초: 상단 안에서 켜졌다 사라진다. 등장 중에도 화면 밖으로 잘리지 않는다. */
const BAND_MS = 2200;
const BAND_MOVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(0)', offset: 0 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.1 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.86 },
  { opacity: 0, transform: 'translateY(0)', offset: 1 },
];
/** 새 스킬 습득 띠 2.6초(D1e): 레벨업 띠 바로 아래 가는 띠가 내려와 머물다 사라진다 */
const SKILL_BAND_MS = 2600;
const SKILL_BAND_MOVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(0)', offset: 0 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.1 },
  { opacity: 1, transform: 'translateY(0)', offset: 0.88 },
  { opacity: 0, transform: 'translateY(0)', offset: 1 },
];
const SKILL_BAND_CALM: Keyframe[] = [{ opacity: 0, offset: 0 }, { opacity: 1, offset: 0.08 }, { opacity: 1, offset: 0.88 }, { opacity: 0, offset: 1 }];
const BAND_CALM: Keyframe[] = [{ opacity: 0, offset: 0 }, { opacity: 1, offset: 0.08 }, { opacity: 1, offset: 0.86 }, { opacity: 0, offset: 1 }];
const BAND_SWEEP: Keyframe[] = [{ opacity: 1, transform: 'translateX(-120%)' }, { opacity: 1, transform: 'translateX(380%)', offset: 0.95 }, { opacity: 0, transform: 'translateX(380%)' }];
/** 같은 EXP 알림을 이 간격보다 자주 띄우지 않고 합산한다(ms) */
const XP_TICK_GAP = 350;

/** 효과 하나를 미리 만든다(재생하지 않은 상태). fill 없음: 끝나거나 취소되면 원래 스타일(opacity 0 등)로 돌아간다 */
function prepare(target: Element, frames: Keyframe[], duration: number, easing: string): Animation {
  return new Animation(new KeyframeEffect(target, frames, { duration, easing }), document.timeline);
}

/** 처음부터 다시 재생(진행 중이어도 되감는다) */
function restart(a: Animation): void {
  a.currentTime = 0;
  a.play();
}

function setFrames(a: Animation, frames: Keyframe[]): void {
  (a.effect as KeyframeEffect).setKeyframes(frames);
}

// ── 원화 측정값(qa/m1/hud3/tools/measure.py, 원본 픽셀) ─────────────────────────
const BAR_W = 400;
/** 초상 테두리 288×280: 구멍 중심(비율)·지름(가로 비율) */
const PF = { w: 288, h: 280, cx: 0.5645, cy: 0.4762, hole: 0.7264 } as const;
const PF_W = 128;
/** 좌상단 원화 묶음만 왼쪽 1/3 폭·상단 112px 여백 안으로 제한한다. */
export function topLeftHudScale(w: number, u: number): number {
  return Math.min(u, (w / 3 - 12) / (104 + BAR_W), 106 / Math.round((PF_W * PF.h) / PF.w));
}
/** 미니맵 링 320×319 */
const MR = { w: 320, h: 319, cx: 0.497, cy: 0.5103, hole: 0.706 } as const;
const MR_W = 168;
const PO_W = 352;

const CLOCK_ICON = '<svg viewBox="0 0 24 24" width="17" height="17" style="vertical-align:-3px;margin-right:6px" fill="none" stroke="#ffd76a" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4.5l3 2M9 2.5h6"/></svg>';

interface Bar {
  box: HTMLDivElement;
  fill: HTMLDivElement;
  /** 피해를 늦게 따라오는 밝은 꼬리 */
  trail: HTMLDivElement;
  text: HTMLSpanElement;
  last: number;
  lastMax: number;
  /** 맞았을 때 바 번쩍임(미리 만든 Animation, 화면 리미터를 거쳐서만 재생) */
  flash: Animation;
}

/** 보스 바(이전 CSS 모양 유지) */
function makeBar(parent: HTMLElement, w: number, h: number, color: string, glow: string): Bar {
  const box = el('div', 'position:relative;width:' + w + 'px;height:' + h + 'px;background:#0b1020;border:2px solid ' + C.gold + ';border-radius:4px;overflow:hidden;transform:skewX(-14deg);', parent);
  const trail = el('div', 'position:absolute;left:0;top:0;bottom:0;width:100%;background:#ffe2c8;transition:width .45s ease-out .2s;', box);
  const fill = el('div', 'position:absolute;left:0;top:0;bottom:0;width:100%;background:linear-gradient(' + glow + ',' + color + ');transition:width .12s linear;', box);
  const text = el('span', 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transform:skewX(14deg);font:700 ' + (h - 6) + 'px ' + FONT + ';color:#fff;text-shadow:0 1px 2px #000;', box);
  return { box, fill, trail, text, last: -1, lastMax: -1, flash: prepare(box, FLASH_FULL, HUD_FLASH_MS, 'linear') };
}

/** 바 틀 원화의 칸 안에 들어가는 채움 바. 오른쪽 끝은 틀처럼 뾰족하다 */
/** 얇은 둥근 막대(HUD 정리 2026-10-27): 원화 틀 없이 어두운 바탕 + 색 막대. withText = 오른쪽 안쪽에 "현재 / 최대" */
function makeSlimBar(parent: HTMLElement, x: number, y: number, w: number, h: number, top: string, bottom: string, withText: boolean): Bar {
  const box = el('div', 'position:absolute;left:' + x + 'px;top:' + y + 'px;width:' + w + 'px;height:' + h + 'px;overflow:hidden;border-radius:' + h / 2 + 'px;' +
    'background:rgba(0,0,0,.58);box-shadow:0 1px 3px rgba(0,0,0,.6);', parent);
  const trail = el('div', 'position:absolute;left:0;top:0;bottom:0;width:100%;background:rgba(255,236,210,.85);transition:width .45s ease-out .2s;', box);
  const fill = el('div', 'position:absolute;left:0;top:0;bottom:0;width:100%;background:linear-gradient(180deg,' + top + ' 0%,' + bottom + ' 100%);transition:width .12s linear;', box);
  const text = el('span', 'position:absolute;right:8px;top:0;bottom:0;display:' + (withText ? 'flex' : 'none') + ';align-items:center;font:800 ' + Math.max(9, Math.round(h * 0.8)) + 'px ' + FONT + ';' +
    'color:#fff;letter-spacing:.3px;' + TEXT_OUTLINE, box);
  return { box, fill, trail, text, last: -1, lastMax: -1, flash: prepare(box, FLASH_FULL, HUD_FLASH_MS, 'linear') };
}

function setBar(b: Bar, v: number, max: number): void {
  const iv = Math.round(v);
  if (iv === b.last && max === b.lastMax) return;
  b.last = iv;
  b.lastMax = max;
  const w = (max > 0 ? Math.max(0, Math.min(100, (v / max) * 100)) : 0) + '%';
  b.fill.style.width = w;
  b.trail.style.width = w;
  b.text.textContent = fmtNum(Math.max(0, v)) + ' / ' + fmtNum(max);
}

/** 원화 위에 겹치는 글자: 금색 그라데이션(배경 자르기)에는 text-shadow 대신 drop-shadow를 쓴다 */
const GOLD_SHADOW = 'filter:drop-shadow(0 1px 0 #000) drop-shadow(0 2px 3px rgba(0,0,0,.8));';

export class Hud {
  readonly root: HTMLDivElement;
  private readonly tl: HTMLDivElement;
  private readonly quest: HTMLDivElement;
  private questOn = true;
  /** 용사의 시련 진행 띠(목표 패널 셋째 줄). rev가 바뀔 때만 다시 쓴다(-1 = 숨김) */
  private trialText!: HTMLDivElement;
  private kTrial = -1;
  private readonly tr: HTMLDivElement;
  /** 미니맵 아래 테두리 명판(지역 이름·시계) */
  private readonly stagePanel: HTMLDivElement;
  private readonly minimapBox: HTMLDivElement;
  /** 카메라 초기화(미니맵 왼쪽 아래 작은 그림 단추) */
  private readonly cameraBtn: HTMLButtonElement;
  /** 오른쪽 위 아이콘 메뉴 줄 */
  private readonly menu: MenuBar;
  private topRightKey = '';
  private trOn = true;
  /** 마을 시계(현지 시각 HH:MM) — 분이 바뀔 때만 글자를 쓴다 */
  private lastMinute = -1;
  private readonly comboBox: HTMLDivElement;
  private readonly comboRow: HTMLDivElement;
  private comboLayoutKey = '';
  private readonly bossBox: HTMLDivElement;
  private readonly awakenBox: HTMLDivElement;
  private readonly hpBar: Bar;
  private readonly mpBar: Bar;
  private readonly stageText: HTMLDivElement;
  private readonly clockText: HTMLSpanElement;
  private readonly objText: HTMLDivElement;
  private readonly cntText: HTMLDivElement;
  private readonly questTitle: HTMLDivElement;
  private readonly comboNum: HTMLSpanElement;
  private readonly comboWord: HTMLSpanElement;
  private readonly praise: HTMLDivElement;
  private readonly bossName: HTMLDivElement;
  private readonly bossBar: Bar;
  private readonly bossTag: HTMLDivElement;
  private readonly staggerFill: HTMLDivElement;
  private lastInvul = false;
  private lastStagger = -1;
  /** S5 분노·광폭: 체력바 구간(눈금 왼쪽 = 분노 구간)·눈금 글자·이름 옆 표·전환 알림 띠 */
  private readonly bossRageZone: HTMLDivElement;
  private readonly bossRageZoneText: HTMLSpanElement;
  private readonly bossRageChip: HTMLSpanElement;
  private readonly bossRageBand: HTMLDivElement;
  private readonly bossRageBandHead: HTMLSpanElement;
  private readonly bossRageBandTip: HTMLSpanElement;
  private lastRageAt = -1;
  private lastRageVariant = -1;
  private lastRage = -1;
  private rageBandUntil = 0;
  /** ?qa&bandhold(QA 캡처 전용): 느린 캡처가 띠를 놓치지 않게 길게 둔다 */
  private readonly rageBandMs: number;
  /** HUD 예약 영역(피해 숫자가 비킨다). 크기는 초기 보호 치수/ResizeObserver 값, 위치는 이 HUD가 정한 top·배율 */
  private readonly reserve: ScreenReserve | null;
  private readonly obsW = new Float32Array(4);
  private readonly obsH = new Float32Array(4);
  private reserveDirty = true;
  private reserveVw = -1;
  private reserveU = -1;
  private reserveBoss = false;
  private reserveTarget = false;
  private targetTopPx = TARGET_TOP;
  /** 보스 경직 바 오른쪽의 '가드 브레이크' 글자 */
  private readonly bossGuardLabel: HTMLDivElement;
  // 대상 프레임(방패 고블린·엘리트): 보스가 없을 때 보스 바 자리에 뜬다
  private readonly target = new TargetFrameModel();
  private readonly targetBox: HTMLDivElement;
  private readonly targetName: HTMLDivElement;
  private readonly targetHp: Bar;
  private readonly targetPostureBox: HTMLDivElement;
  private readonly targetPosture: HTMLDivElement;
  private readonly targetGuardLabel: HTMLDivElement;
  private lastTargetOn = false;
  private lastTargetOp = -1;
  private lastTargetName = '';
  private lastTargetPosture = -1;
  private lastTargetBreak = false;
  // 저스트 회피 글자(화면 가운데보다 약간 아래)와 방 정리 회복 숫자
  private readonly justWrap: HTMLDivElement;
  private readonly justText: HTMLDivElement;
  private readonly justAnim: Animation;
  private readonly healText: HTMLDivElement;
  /** D3a 상태 이상 표지 */
  private readonly statusBox: HTMLDivElement;
  private readonly chillChip: HTMLSpanElement;
  private readonly poisonChip: HTMLSpanElement;
  /** 비약 표지(D2c): 계열마다 짧은 이름 + 남은 시간(접속 시간 기준). 체력 바 아래 가장자리 */
  private readonly elixirChips: HTMLSpanElement[] = [];
  private readonly elixirTime: HTMLSpanElement[] = [];
  private readonly elixirLast = new Int32Array(4).fill(-1);
  private kStatusVillage: boolean | null = null;
  private statusTop = '';
  /** 골드 한 줄(마을·던전 공통, 값이 바뀔 때만 쓴다) */
  private readonly goldLine: HTMLDivElement;
  private readonly goldText: HTMLSpanElement;
  private lastGold = '';
  private kStatus = -1;
  private readonly healAnim: Animation;
  // 레벨·EXP: 초상 아래 Lv 판과 얇은 EXP 막대, 얻을 때 막대 옆 '+N EXP'(몹 위가 아니라 막대 곁에서만), 레벨업 때 위쪽 가장자리 띠
  private readonly lvText: HTMLDivElement;
  private readonly lvPlate: HTMLDivElement;
  /** 성장 정보가 있을 때만 Lv 판과 EXP 막대를 보인다(-1 미반영) */
  private lvShown = -1;
  private readonly xpFill: HTMLDivElement;
  private readonly xpBox: HTMLDivElement;
  private readonly xpTick: HTMLDivElement;
  private readonly xpTickAnim: Animation;
  private readonly bandOuter: HTMLDivElement;
  private readonly bandInner: HTMLDivElement;
  private readonly bandHead: HTMLSpanElement;
  private readonly bandTail: HTMLSpanElement;
  private readonly bandSweep: HTMLDivElement;
  private readonly bandAnim: Animation;
  private readonly bandSweepAnim: Animation;
  /** 레벨업 띠 아래 한 줄: 고레벨 EXP 배율이 바뀌는 레벨에서만 보인다(C10-4) */
  private readonly bandNote: HTMLDivElement;
  private readonly bandNoteAnim: Animation;
  /** 경험치 바 곁 고레벨 EXP 보정 표식(배율 1이면 숨김)과 탭하면 뜨는 한 줄 설명 */
  private readonly xpBandBadge: HTMLButtonElement;
  private lastBandBadge = '';
  /** 표식을 누르면 위쪽 가장자리에 4.5초 뜨는 한 줄 설명 띠 */
  private readonly bandHelp: HTMLDivElement;
  private readonly bandHelpAnim: Animation;
  private helpOn = false;
  /** 새 스킬 습득 띠(레벨업 띠 바로 아래, 같은 바깥 상자 안) */
  private readonly skillBand: HTMLDivElement;
  private readonly skillBandHead: HTMLSpanElement;
  private readonly skillBandTail: HTMLSpanElement;
  /** 새 스킬 띠의 스킬 그림 칸(승인 그림 skill-hud-v2, 띠를 띄울 때만 바꾼다) */
  private readonly skillBandIcon: HTMLSpanElement;
  private skillBandArt: SkillArt | null = null;
  private readonly skillBandAnim: Animation;
  private bandCovered = false;
  private bandWidth = -1;
  /** 한 묶음에 여러 건 온 새 스킬 띠를 차례로 보이는 대기열(앞 띠를 덮어쓰지 않는다) */
  private readonly skillBandQueue = new SkillBandQueue();
  private lastLv = -1;
  /** 마지막으로 계산한 growth.revision·레벨(같으면 treeHeader를 다시 만들지 않는다: 프레임 루프 할당 금지) */
  private lastGRev = -1;
  private lastXpPct = -1;
  private xpSnap = false;
  /** 모아 둔 EXP(짧은 간격의 처치 EXP를 한 번에 보인다)와 마지막으로 보인 시각 */
  private xpAcc = 0;
  private xpTickMs = 0;
  // 방 요령 토스트(R2 지역 요령, REGIONS[mapId].roomTips): 4.5초 뒤 사라진다. 입력을 막지 않는다
  private readonly tipBox: HTMLDivElement;
  private readonly tipText: HTMLDivElement;
  private readonly tipHead: HTMLDivElement;
  private readonly tipGate = new RoomTipGate();
  /** 토스트가 사라질 렌더 벽시계(ms), 꺼져 있으면 0 */
  private tipUntil = 0;
  /** 요령 상자의 현재 배율(연계 안내 줄이 피할 실제 크기) */
  private tipScale = 1;
  /** 요령 토스트 자리 캐시(화면 크기·알약 사각형·기본 배율·글자 회차가 같으면 다시 재지 않는다)와 자동 이동 알약(AutoQuestUi, 찾으면 보관) */
  private kTipW = -1;
  private kTipSx = -1;
  private kTipSy = -1;
  private kTipSh = -1;
  private kTipH = -1;
  private kTipBase = -1;
  private kTipSerial = -1;
  private tipSerial = 0;
  private tipBase = 1;
  private autoStatusEl: HTMLElement | null = null;
  /** 마지막으로 쓴 회복량(같은 값이면 문자열을 다시 만들지 않는다) */
  private lastHeal = -1;
  private readonly bossGuardAnim: Animation;
  private readonly targetGuardAnim: Animation;
  /** 바 번쩍임 화면 리미터(영웅·보스·대상 공유, COMBAT-FEEL 5.1) */
  private readonly flashGate = new HudFlashGate();
  /** 이번 update의 렌더 벽시계(ms). 이벤트 처리와 대상 프레임이 같은 값을 쓴다 */
  private nowMs = 0;
  /** 섬광 약화·동작 줄이기 현재 반영 상태(-1 미반영) */
  private reduceFlashOn = -1;
  private reduceMotionOn = -1;
  private readonly motionQuery: MediaQueryList | null;
  private readonly awakenFill: HTMLDivElement;
  private readonly awakenLabel: HTMLDivElement;
  private readonly awakenBar: HTMLDivElement;
  /** 터치 세로 배치에서 각성 게이지 자리(키 문자열로 바뀔 때만 다시 쓴다) */
  private anchorKey = '';
  private awakenRaid = false;
  private awakenShift = -1;
  private raidRev = -1;
  private readonly raidObs: HudRect[] = [{ x: 0, y: 0, w: 0, h: 0 }, { x: 0, y: 0, w: 0, h: 0 }];
  private raidHudEl: HTMLElement | null = null;
  private readonly raidTl: HudRect = { x: 0, y: 0, w: 0, h: 0 };
  private readonly rvTl: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly rvGold: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly rvBoss: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly rvParty: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly rvQuest: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly raidBelow = { top: 0, limit: 0 };
  private readonly mmCanvas: HTMLCanvasElement;
  private readonly mmCtx: CanvasRenderingContext2D;
  private readonly hurt: HTMLDivElement;
  private readonly lowHp: HTMLDivElement;
  /** 0 꺼짐, 1 경고(30% 이하), 2 위급(15% 이하) */
  private lastLow = -1;
  private readonly skillbar: SkillBar | null = null;
  private readonly link: LinkHud;
  private readonly rampage: RampageHud;
  private readonly linkHint: LinkHint;
  private frame = 0;
  private cursor = 0;
  private lastCombo = -1;
  private lastComboOp = -1;
  private lastClock = -1;
  private lastStage = '';
  private lastObj = '';
  /** 초상 이미지와 지금 보이는 장비 단계(장착 갑옷이 바뀔 때만 src를 바꾼다) */
  private portraitImg: HTMLImageElement | null = null;
  private portraitTier = 1;
  /** 이름 판 글자와 그 이름의 영웅(c022: 영웅을 바꾸면 이름·초상을 바꾼다) */
  private nameText: HTMLDivElement | null = null;
  private shownHero: PlayableHeroId = 'kael';
  /** 이름표 옆 회차 별(레인 R) */
  private starText: HTMLSpanElement | null = null;
  private shownCycle = 0;
  private portraitFallback: HTMLElement | null = null;
  private lastAwaken = -1;
  private lastScale = -1;
  private lastTopLeftScale = -1;
  private comboAnim: Animation | null = null;
  private praiseAnim: Animation | null = null;
  private hurtAnim: Animation | null = null;

  /** 터치 기기: 키보드 안내를 빼고, 칭찬 문구를 콤보 왼쪽에 둔다(아래는 터치 버튼) */
  constructor(parent: HTMLElement, private readonly view: GameView, send: (c: GameCommand) => void, private readonly touch = false, input?: InputSink, reserve: ScreenReserve | null = null) {
    this.reserve = reserve;
    this.root = el('div', 'position:absolute;inset:0;pointer-events:none;font-family:' + FONT + ';', parent);
    if (!document.getElementById(HUD_STYLE_ID)) {
      const st = document.createElement('style');
      st.id = HUD_STYLE_ID;
      st.textContent = HUD_CSS;
      document.head.appendChild(st);
    }

    // 체력 낮음 경고(가장 아래 층: 다른 HUD 요소를 가리지 않는다)
    this.lowHp = el('div', 'position:absolute;inset:0;box-shadow:inset 0 0 120px 24px rgba(200,10,30,.78);opacity:0;display:none;', this.root);
    this.lowHp.dataset.testid = 'low-hp';

    // ── 좌상단: 초상 테두리 + 이름 + 체력/MP 바 틀 ──
    this.tl = el('div', 'position:absolute;left:6px;top:4px;width:' + (104 + BAR_W) + 'px;height:' + Math.round((PF_W * PF.h) / PF.w) + 'px;transform-origin:0 0;', this.root);
    // 월드 이름표가 영웅 초상·체력 바·목표·미니맵 밑으로 들어가 잘리지 않도록 패널 영역으로 알린다(render/env/label placeLabels)
    setPanelRect('hud-hero', elementRect(this.tl));
    // 인앱 브라우저 안내 띠(inAppBanner.ts)가 위쪽 왼편·오른편 묶음 사이 빈 띠를 재는 데 쓴다
    this.tl.dataset.testid = 'hud-top-left';
    // HUD 정리(2026-10-27 시안 A): 원화 틀 대신 얇은 원형 초상 + 이름·레벨 한 줄 + 얇은 HP·MP·EXP 막대 + 골드 한 줄(설계 px, tl 배율로 줄어든다)
    const SL = 112, SW = 300;
    this.hpBar = makeSlimBar(this.tl, SL, 44, SW, 16, '#ff6a5a', '#c41e2a', true);
    this.mpBar = makeSlimBar(this.tl, SL, 66, SW, 10, '#7ec4ff', '#2a6ad8', false);
    // 이름 줄: 이름 + 레벨(레벨 판은 같은 줄 오른쪽 글자로)
    const name = el('div', 'position:absolute;left:' + SL + 'px;top:6px;height:32px;display:flex;align-items:baseline;gap:10px;', this.tl);
    name.dataset.testid = 'hud-nameplate';
    this.nameText = el('div', 'font:900 24px ' + FONT + ';color:#fff;letter-spacing:.5px;' + TEXT_OUTLINE, name, '카엘');
    this.starText = el('span', 'display:none;margin-left:6px;font:900 17px ' + FONT + ';letter-spacing:1px;' + TEXT_OUTLINE, name, '');
    this.starText.dataset.testid = 'hud-rebirth-stars';
    // 지역 상태 이상(D3a)·영약: 골드 줄 오른쪽 작은 표지. 화면 가장자리(왼쪽 위 HUD 안)라 플레이 영역을 가리지 않는다
    this.statusBox = el('div', 'position:absolute;left:' + (SL + 150) + 'px;top:98px;display:none;gap:4px;pointer-events:none;', this.tl);
    this.statusBox.dataset.testid = 'hud-status';
    this.statusTop = this.statusBox.style.top;
    setPanelRect('hud-status', elementRect(this.statusBox));
    this.chillChip = el('span', 'display:none;padding:1px 7px;border-radius:9px;font:900 12px ' + FONT + ';color:#0c2238;background:linear-gradient(180deg,#d8f2ff,#7cc8ff);border:1px solid #e8f8ff;', this.statusBox, '둔화');
    this.poisonChip = el('span', 'display:none;padding:1px 7px;border-radius:9px;font:900 12px ' + FONT + ';color:#14280a;background:linear-gradient(180deg,#d6ff9a,#78c83a);border:1px solid #eaffc8;', this.statusBox, '중독');
    for (let k = 0; k < ELIXIRS.length; k++) {
      const chip = el('span', 'display:none;align-items:center;gap:3px;padding:1px 6px;border-radius:9px;font:900 11px ' + FONT + ';color:#fff;background:rgba(10,16,32,.7);' +
        'border:1px solid ' + ELIXIR_TINT[k] + ';white-space:nowrap;', this.statusBox);
      chip.dataset.testid = 'hud-elixir-' + ELIXIRS[k].fam;
      el('span', 'color:' + ELIXIR_TINT[k] + ';', chip, ELIXIRS[k].short);
      this.elixirTime.push(el('span', 'font:800 11px ' + FONT_NUM + ';color:#e6ecff;', chip, ''));
      this.elixirChips.push(chip);
    }
    // 방 정리 회복: 체력 막대 오른쪽 위에 초록 '+N'
    this.healText = el('div', 'position:absolute;left:' + (SL + SW + 8) + 'px;top:34px;opacity:0;white-space:nowrap;pointer-events:none;font:900 24px ' + FONT_NUM + ';font-style:italic;letter-spacing:1px;color:#6dff8a;' + TEXT_OUTLINE, this.tl, '');
    // 초상: 얇은 금테 원(원화 테두리 없음)
    const portrait = el('div', 'position:absolute;left:4px;top:4px;width:96px;height:96px;border-radius:50%;overflow:hidden;box-sizing:border-box;border:3px solid ' + C.gold + ';' +
      'background:radial-gradient(circle at 35% 30%,#f5f7ff,#9aa6c8 55%,#4a567a);display:flex;align-items:center;justify-content:center;box-shadow:0 3px 10px rgba(0,0,0,.55);', this.tl);
    const fallbackK = el('span', 'font:900 46px ' + FONT_NUM + ';color:#b3202f;text-shadow:0 2px 0 #fff;display:none;', portrait, 'K');
    this.portraitFallback = fallbackK;
    this.portraitImg = hudImg('portrait', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;', portrait, () => { fallbackK.style.display = 'block'; });
    // 레벨: 이름 줄 오른쪽 금색 글자(성장이 꺼진 빌드에서는 숨긴다)
    const lvPlate = el('div', 'display:flex;align-items:baseline;', name);
    this.lvPlate = lvPlate;
    lvPlate.dataset.testid = 'hud-level';
    this.lvText = el('div', 'font:900 18px ' + FONT_NUM + ';letter-spacing:.5px;white-space:nowrap;color:' + C.gold + ';' + TEXT_OUTLINE, lvPlate, 'Lv.1');
    // EXP: MP 아래 아주 얇은 금색 막대
    this.xpBox = el('div', 'position:absolute;left:' + SL + 'px;top:82px;width:' + SW + 'px;height:5px;box-sizing:border-box;border-radius:3px;overflow:hidden;background:rgba(0,0,0,.55);', this.tl);
    this.xpBox.dataset.testid = 'hud-xp';
    this.xpBox.setAttribute('role', 'progressbar');
    this.xpBox.setAttribute('aria-label', '경험치');
    this.xpFill = el('div', 'height:100%;width:0%;background:linear-gradient(90deg,#c88a1a,#ffe07a);transition:width .35s ease-out;', this.xpBox);
    this.xpTick = el('div', 'position:absolute;left:' + SL + 'px;top:118px;opacity:0;white-space:nowrap;pointer-events:none;font:900 15px ' + FONT_NUM + ';letter-spacing:.5px;color:#ffe07a;' + TEXT_OUTLINE, this.tl, '');
    // 골드: 막대 아래 한 줄(마을 버튼 줄에서 옮겨 왔다)
    this.goldLine = el('div', 'position:absolute;left:' + SL + 'px;top:94px;height:24px;display:none;align-items:center;gap:7px;', this.tl);
    this.goldLine.dataset.testid = 'hud-gold';
    el('span', 'width:18px;height:18px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff3b0,#e0a12f 60%,#8a5a12);box-shadow:0 1px 3px rgba(0,0,0,.6);', this.goldLine);
    this.goldText = el('span', 'font:800 20px ' + FONT_NUM + ';letter-spacing:.5px;color:#fff;' + TEXT_OUTLINE, this.goldLine, '');
    this.xpTick.dataset.testid = 'hud-xp-tick';
    // 고레벨 EXP 보정 표식: 골드 줄 아래 오른쪽(왼쪽 위 HUD 안, 가장자리). 눌러서 규칙 한 줄을 본다
    this.xpBandBadge = el('button', 'position:absolute;left:' + (SL + 150) + 'px;top:118px;display:none;width:max-content;box-sizing:border-box;margin:0;padding:1px 7px;border:1px solid rgba(255,224,122,.55);border-radius:9px;background:rgba(16,22,42,.78);white-space:nowrap;pointer-events:auto;cursor:pointer;touch-action:manipulation;font:800 12px ' + FONT + ';letter-spacing:.2px;color:#ffe07a;', this.tl, '') as HTMLButtonElement;
    this.xpBandBadge.dataset.testid = 'hud-xp-band';
    this.xpBandBadge.type = 'button';
    this.xpBandBadge.title = ''; // 규칙 설명은 Lv45부터만 채운다(xpBandHelpText) — 신규에게 이른 환생·EXP 감소 안내를 DOM에도 두지 않는다
    this.xpBandBadge.addEventListener('pointerdown', (e) => e.preventDefault());
    this.xpBandBadge.addEventListener('keydown', (e) => { if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') e.stopPropagation(); });
    // 눌러서 규칙 한 줄: 레벨업 띠와 같은 위쪽 가장자리 얇은 띠(bandHelp)에 4.5초 보인다
    this.xpBandBadge.addEventListener('click', () => restart(this.bandHelpAnim));
    // 설명 띠: 골드 줄·표식 자리(경험치 바 아래 같은 가장자리 칸)에 4.5초 겹쳐 뜨고, 그동안 골드 줄·표식은 가린다 — 다른 HUD와 겹치지 않는다
    this.bandHelp = el('div', 'position:absolute;left:' + SL + 'px;top:96px;width:' + SW + 'px;box-sizing:border-box;padding:2px 8px;opacity:0;pointer-events:none;border-radius:7px;background:rgba(16,22,42,.94);border:1px solid rgba(255,224,122,.6);font:700 11.5px/1.3 ' + FONT + ';color:#fff;word-break:keep-all;' + TEXT_OUTLINE, this.tl, '');
    this.bandHelp.dataset.testid = 'hud-xp-band-help';
    this.bandHelp.setAttribute('role', 'status');

    // ── 좌측 목표 패널(왼쪽 방패 문장 + 남색 본체) ──
    this.quest = el('div', 'position:absolute;left:8px;top:52px;width:' + PO_W + 'px;height:52px;box-sizing:border-box;background:rgba(16,22,42,.88);border-radius:8px;transform-origin:0 0;', this.root);
    setPanelRect('hud-objective', elementRect(this.quest));
    this.quest.dataset.testid = 'hud-objective';
    // HUD 정리(2026-10-27): 원화 판 대신 옅어지는 반투명 띠
    this.quest.style.background = 'linear-gradient(90deg,rgba(8,12,24,.66) 0%,rgba(8,12,24,.58) 70%,rgba(8,12,24,0) 100%)';
    const qText = el('div', 'position:absolute;left:12px;right:8px;top:2px;bottom:2px;' +
      'display:flex;flex-direction:column;justify-content:center;line-height:1.18;', this.quest);
    // 첫 줄: 제목(금색) + 처치 수(오른쪽), 둘째 줄: 목표 문장
    const qHead = el('div', 'display:flex;align-items:baseline;justify-content:space-between;gap:6px;', qText);
    this.questTitle = el('div', 'font:900 12px ' + FONT + ';letter-spacing:.2px;white-space:normal;word-break:keep-all;' + GOLD_TEXT + GOLD_SHADOW, qHead, '');
    this.cntText = el('div', 'font:900 13px ' + FONT_NUM + ';font-style:italic;color:#fff;letter-spacing:.5px;white-space:nowrap;' + TEXT_OUTLINE, qHead, '');
    this.objText = el('div', 'margin-top:2px;font:600 12px ' + FONT + ';color:#e6ecff;' + TEXT_OUTLINE, qText, '');
    // 용사의 시련 판: 목표 패널 안 셋째 줄(화면 가운데·주인공·앞길을 가리지 않는다). 시련 판이 아니면 숨김
    this.trialText = el('div', 'display:none;margin-top:2px;font:800 12px ' + FONT + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' + TEXT_OUTLINE, qText, '');
    this.trialText.dataset.testid = 'hud-trial';

    // ── 오른쪽 위(2026-10-27 개편, hudMenuLayout.ts): 아이콘 메뉴 줄 + 큰 미니맵 + 미니맵 테두리 명판(지역 이름·시계) + 카메라 초기화.
    // 일시정지·전체 화면·시야 확대 단추는 따로 띄우지 않고 설정(톱니) → 일시정지 창 안으로 옮겼다(Esc·F·휠/+−·두 손가락 줌은 그대로).
    this.tr = el('div', 'position:absolute;left:0;top:0;pointer-events:none;', this.root);
    this.tr.dataset.testid = 'hud-top-right';
    const mmBox = this.minimapBox = el('div', 'position:absolute;width:' + MR_W + 'px;aspect-ratio:' + MR.w + '/' + MR.h + ';pointer-events:auto;cursor:pointer;', this.tr);
    mmBox.dataset.testid = 'hud-minimap';
    mmBox.setAttribute('role', 'button');
    mmBox.setAttribute('aria-label', '지도 열기 (M)');
    mmBox.setAttribute('data-tip', '지도 (M)');
    // 마을: 누르면 큰 지도(스테이지 지도, M 키와 같은 경로). 던전은 지도 창이 없어 아무 일도 하지 않는다
    mmBox.addEventListener('click', () => { if (view.flow.scene === Scene.Village) window.dispatchEvent(new CustomEvent(MENU_EVENT, { detail: 'map' })); });
    setPanelRect('hud-minimap', elementRect(mmBox));
    const md = MR.hole * 1.03, mdh = md * (MR.w / MR.h);
    const mmWrap = el('div', 'position:absolute;left:' + ((MR.cx - md / 2) * 100).toFixed(2) + '%;top:' + ((MR.cy - mdh / 2) * 100).toFixed(2) + '%;width:' + (md * 100).toFixed(2) + '%;height:' + (mdh * 100).toFixed(2) + '%;' +
      'border-radius:50%;overflow:hidden;background:#0c1322;box-sizing:border-box;', mmBox);
    this.mmCanvas = el('canvas', 'width:100%;height:100%;display:block;', mmWrap);
    this.mmCanvas.width = MM;
    this.mmCanvas.height = MM;
    this.mmCtx = this.mmCanvas.getContext('2d')!;
    // 가장자리 그늘(링 안쪽으로 들어가 보이게)
    el('div', 'position:absolute;inset:0;border-radius:50%;box-shadow:inset 0 0 14px 4px rgba(0,0,0,.75);pointer-events:none;', mmWrap);
    const mmN = el('div', 'position:absolute;left:0;right:0;top:3px;text-align:center;font:700 11px ' + FONT + ';color:#fff;text-shadow:0 1px 2px #000;display:none;', mmWrap, 'N');
    hudImg('minimapRing', 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;', mmBox, () => {
      mmWrap.style.border = '3px solid ' + C.gold;
      mmN.style.display = 'block';
    });
    // 명판: 미니맵 아래 테두리에 걸친 남색 금테 띠 하나에 지역 이름 · 시계(던전 = 경과 시간, 마을 = 현지 시각)
    const stagePanel = this.stagePanel = el('div', 'position:absolute;box-sizing:border-box;display:flex;align-items:center;justify-content:center;gap:6px;padding:0 10px;border-radius:999px;' +
      'border:1.5px solid ' + C.gold + ';background:linear-gradient(180deg,rgba(30,40,72,.95),rgba(12,17,34,.95));box-shadow:0 0 0 2px rgba(9,12,26,.8),0 3px 8px rgba(0,0,0,.5);white-space:nowrap;overflow:hidden;', this.tr);
    stagePanel.dataset.testid = 'hud-stage-plate';
    this.stageText = el('div', 'min-width:0;overflow:hidden;text-overflow:ellipsis;font:800 13px ' + FONT + ';color:#fff;white-space:nowrap;' + TEXT_OUTLINE, stagePanel, '');
    const clockRow = el('div', 'flex:0 0 auto;display:flex;align-items:center;', stagePanel);
    clockRow.insertAdjacentHTML('afterbegin', CLOCK_ICON.replace('width="17" height="17"', 'width="13" height="13"').replace('margin-right:6px', 'margin-right:3px'));
    this.clockText = el('span', 'font:900 13px ' + FONT_NUM + ';letter-spacing:.5px;' + GOLD_TEXT + GOLD_SHADOW, clockRow, '00:00');
    this.clockText.dataset.testid = 'hud-clock';
    // 카메라 초기화(C): 미니맵 옆 작은 그림 단추. 시야 확대·축소는 휠·+/−·두 손가락·설정 슬라이더
    const cam = this.cameraBtn = el('button', 'position:absolute;box-sizing:border-box;padding:0;border-radius:50%;cursor:pointer;pointer-events:auto;touch-action:manipulation;' +
      'border:2px solid ' + C.gold + ';background:radial-gradient(circle at 40% 35%,#33416c,#0e1428 75%);box-shadow:0 2px 6px rgba(0,0,0,.7),inset 0 0 6px rgba(0,0,0,.6);', this.tr);
    cam.type = 'button';
    cam.dataset.testid = 'camera-reset';
    // 키 표시는 키 배치를 따른다(액션식 C, PC RPG식 Home — shared/keymap.ts)
    const camKey = (): void => {
      const k = cameraResetKeyLabel(keymap.preset);
      cam.setAttribute('aria-label', '카메라 초기화' + (touch ? '' : ' (' + k + ')'));
      cam.setAttribute('data-tip', '카메라 초기화 (' + k + ')');
    };
    camKey(); keymap.subscribe(camKey);
    const camImg = el('img', 'position:absolute;inset:8%;width:84%;height:84%;object-fit:contain;pointer-events:none;', cam);
    camImg.src = assetUrl('ui/icons/menu-camera.webp'); camImg.alt = ''; camImg.draggable = false;
    camImg.onerror = () => { camImg.remove(); cam.textContent = '⟲'; cam.style.color = '#ffe7a0'; cam.style.font = '900 20px ' + FONT; };
    cam.addEventListener('pointerdown', (e) => e.preventDefault());
    cam.addEventListener('keydown', (e) => { if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') e.stopPropagation(); });
    cam.addEventListener('click', () => window.dispatchEvent(new CustomEvent('dh-camera-yaw', { detail: { reset: true } })));
    // 아이콘 메뉴 줄(마을 9칸 · 던전 톱니 하나)
    this.menu = new MenuBar(this.tr, view, send, touch);
    this.layoutTopRight(viewW(), viewH(), 1);

    // ── 레벨업 띠: 화면 위쪽 가장자리 가운데에 걸린다(플레이 영역 안쪽을 덮지 않는다). 금빛 훑기는 동작 줄이기에서 뺀다.
    // 결과·패배 화면(Screens, 나중에 만들어진 형제 층) 위에서도 보이게 HUD 루트가 아니라 부모에 직접 붙이고 z-index를 높인다
    this.bandOuter = el('div', 'position:absolute;z-index:60;left:50%;top:0;width:470px;transform-origin:50% 0;transform:translateX(-50%);pointer-events:none;', parent);
    this.bandInner = el('div', 'position:relative;box-sizing:border-box;height:44px;padding:0 24px;display:flex;align-items:center;justify-content:center;gap:12px;overflow:hidden;opacity:0;white-space:nowrap;' +
      'background:linear-gradient(180deg,rgba(40,30,8,.94),rgba(16,22,42,.94));border:2px solid ' + C.gold + ';border-top:0;border-radius:0 0 14px 14px;' +
      'box-shadow:0 4px 14px rgba(0,0,0,.55),0 0 18px rgba(242,193,78,.45),inset 0 -2px 0 rgba(255,215,106,.35);', this.bandOuter);
    this.bandInner.dataset.testid = 'levelup-band';
    this.bandInner.setAttribute('role', 'status');
    this.bandInner.setAttribute('aria-live', 'polite');
    this.bandNote = el('div', 'position:relative;box-sizing:border-box;height:24px;margin:0 auto;width:max-content;max-width:100%;padding:0 14px;display:flex;align-items:center;opacity:0;white-space:nowrap;font:800 14px ' + FONT + ';color:#ffe07a;' +
      'background:rgba(16,22,42,.92);border:1.5px solid ' + C.gold + ';border-top:0;border-radius:0 0 10px 10px;' + TEXT_OUTLINE, this.bandOuter, '');
    this.bandNote.dataset.testid = 'levelup-xp-band-note';
    this.bandSweep = el('div', 'position:absolute;top:0;bottom:0;left:0;width:34%;opacity:0;pointer-events:none;background:linear-gradient(100deg,rgba(255,236,170,0),rgba(255,236,170,.7) 50%,rgba(255,236,170,0));', this.bandInner);
    this.bandHead = el('span', 'position:relative;font:900 24px ' + FONT_NUM + ';letter-spacing:3px;' + GOLD_TEXT + GOLD_SHADOW, this.bandInner, '레벨 상승');
    this.bandTail = el('span', 'position:relative;font:800 17px ' + FONT + ';color:#fff;' + TEXT_OUTLINE, this.bandInner, '');
    // 새 스킬 습득 띠: 레벨업 띠 바로 아래. 위쪽 가장자리에 붙은 가는 띠라 플레이 영역(화면 가운데)을 덮지 않는다
    this.skillBand = el('div', 'position:absolute;left:0;top:0;box-sizing:border-box;width:100%;margin:0;height:32px;padding:0 12px;display:flex;align-items:center;justify-content:center;gap:9px;opacity:0;white-space:nowrap;' +
      'background:linear-gradient(180deg,rgba(14,26,52,.94),rgba(10,16,34,.94));border:1.5px solid #8ac2ff;border-radius:0 0 12px 12px;border-top:0;' +
      'box-shadow:0 3px 10px rgba(0,0,0,.55),0 0 14px rgba(138,194,255,.4);', this.bandOuter);
    this.skillBand.dataset.testid = 'skill-learned-band';
    this.skillBand.setAttribute('role', 'status');
    this.skillBand.setAttribute('aria-live', 'polite');
    this.skillBandIcon = el('span', 'position:relative;flex:0 0 26px;width:26px;height:26px;border-radius:5px;overflow:hidden;border:1px solid #8ac2ff;background:#071321;display:none;', this.skillBand);
    this.skillBandIcon.dataset.testid = 'skill-learned-icon';
    this.skillBandHead = el('span', 'font:900 14px ' + FONT + ';letter-spacing:2px;color:#bfe0ff;' + TEXT_OUTLINE, this.skillBand, '새 스킬');
    this.skillBandTail = el('span', 'font:800 14px ' + FONT + ';color:#fff;' + TEXT_OUTLINE, this.skillBand, '');
    this.skillBandTail.style.overflow = 'hidden'; this.skillBandTail.style.textOverflow = 'ellipsis';

    // ── 콤보(콘셉트 "87 COMBO": 흰빛→진홍 그라데이션 숫자, 기울어진 붓질 띠) ──
    this.comboBox = el('div', 'position:absolute;left:8px;top:172px;text-align:right;display:none;', this.root);
    this.comboBox.dataset.testid = 'hud-combo';
    const crow = this.comboRow = el('div', 'position:relative;isolation:isolate;display:flex;align-items:baseline;justify-content:flex-end;gap:6px;line-height:1.2;', this.comboBox);
    el('div', 'position:absolute;left:4px;right:4px;top:34%;height:56%;z-index:-1;transform:rotate(-4deg);' +
      'background:linear-gradient(90deg,rgba(120,0,14,0) 0%,rgba(170,10,26,.88) 18%,rgba(90,0,12,.92) 78%,rgba(60,0,8,0) 100%);' +
      'clip-path:polygon(0 38%,6% 12%,22% 18%,40% 0,62% 10%,82% 2%,100% 30%,96% 72%,78% 100%,55% 86%,30% 100%,10% 82%);', crow);
    const RED_TEXT = 'background:linear-gradient(180deg,#ffffff 0%,#ffe0e0 28%,#ff4152 60%,#a00616 100%);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-stroke:2px #3a0006;';
    this.comboNum = el('span', 'font:900 88px ' + FONT_NUM + ';font-style:italic;line-height:1;' + RED_TEXT +
      'filter:drop-shadow(0 4px 0 #4a0010) drop-shadow(0 0 12px rgba(255,40,60,.75));display:inline-block;transform-origin:100% 80%;', crow, '0');
    this.comboWord = el('span', 'font:900 36px ' + FONT_NUM + ';font-style:italic;letter-spacing:1px;' + RED_TEXT.replace('2px #3a0006', '1.5px #3a0006') +
      'filter:drop-shadow(0 3px 0 #4a0010) drop-shadow(0 0 8px rgba(255,40,60,.7));', crow, 'COMBO');
    // 칭찬도 숫자 아래 같은 띠 안에 남긴다. 화면 가운데로 펼쳐지는 별도 꼬리를 만들지 않는다.
    this.praise = el('div', 'position:relative;margin-top:4px;line-height:1.2;font:900 20px ' + FONT_NUM + ';font-style:italic;letter-spacing:1px;' + GOLD_TEXT +
      '-webkit-text-stroke:1.5px #4a2200;filter:drop-shadow(0 3px 0 #6a3400) drop-shadow(0 0 14px rgba(255,190,60,.8));white-space:nowrap;opacity:0;transform-origin:100% 50%;', this.comboBox, '');

    // 방 요령 토스트: 왼쪽 목표 패널 바로 아래(보스 바·대상 프레임·콤보와 겹치지 않는다). 위치·배율은 update에서 맞춘다
    this.tipBox = el('div', 'position:absolute;left:8px;top:240px;width:300px;box-sizing:border-box;padding:9px 14px 10px;border:2px solid ' + C.gold + ';border-radius:10px;' +
      'background:linear-gradient(180deg,rgba(26,34,56,.95),rgba(16,22,42,.95));box-shadow:0 3px 10px rgba(0,0,0,.5);transform-origin:0 0;opacity:0;visibility:hidden;pointer-events:none;', this.root);
    this.tipBox.dataset.testid = 'room-tip';
    this.tipBox.setAttribute('role', 'status');
    this.tipBox.setAttribute('aria-live', 'polite');
    this.tipHead = el('div', 'font:900 12px ' + FONT + ';letter-spacing:2px;color:' + C.gold + ';margin-bottom:3px;', this.tipBox, '요령');
    this.tipText = el('div', 'font:700 15px/1.42 ' + FONT + ';color:#fff;word-break:keep-all;' + TEXT_OUTLINE, this.tipBox, '');

    // 보스 HP 바
    // 왼쪽 위 체력 틀(가로 ~510px)과 겹치지 않게 그 아래 높이에 둔다
    this.bossBox = el('div', 'position:absolute;left:50%;top:' + BOSS_TOP + 'px;transform-origin:50% 0;display:none;flex-direction:column;align-items:center;gap:3px;', this.root);
    this.bossBox.dataset.testid = 'boss-bar';
    // 이름 줄: 이름 + (분노·광폭에 들어가면) 오른쪽 작은 표
    const nameRow = el('div', 'display:flex;align-items:center;gap:8px;', this.bossBox);
    this.bossName = el('div', 'font:900 19px ' + FONT + ';letter-spacing:3px;' + GOLD_TEXT + GOLD_SHADOW, nameRow, '');
    this.bossRageChip = el('span', 'display:none;font:900 13px ' + FONT + ';letter-spacing:2px;color:#fff;border-radius:6px;padding:1px 9px;text-shadow:0 1px 2px #000;', nameRow, '');
    this.bossRageChip.dataset.testid = 'boss-rage-chip';
    this.bossBar = makeBar(this.bossBox, BOSS_BAR_W, 22, '#c0202c', '#ff7a5a');
    // S5 분노·광폭 구간: 체력바 왼쪽 끝 ~ 문턱까지 빗금 띠 + 문턱 눈금(오른쪽 테두리) + 구간 이름(체력 숫자와 겹치지 않는 왼쪽 칸)
    this.bossRageZone = el('div', 'display:none;position:absolute;left:0;top:0;bottom:0;box-sizing:border-box;pointer-events:none;border-right:3px solid #fff;', this.bossBar.box);
    this.bossRageZone.dataset.testid = 'boss-rage-zone';
    this.bossRageZoneText = el('span', 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transform:skewX(14deg);font:900 12px ' + FONT + ';letter-spacing:1px;color:#fff;' + TEXT_OUTLINE, this.bossRageZone, '');
    // 경직 게이지(금색 얇은 바)와 무적 표시
    const stWrap = el('div', 'position:relative;', this.bossBox);
    const stBox = el('div', 'width:' + STAGGER_W + 'px;height:' + POSTURE_H + 'px;background:#0b1020;border:' + POSTURE_BORDER + 'px solid ' + C.gold + ';border-radius:3px;overflow:hidden;', stWrap);
    this.staggerFill = el('div', 'height:100%;width:0%;background:linear-gradient(90deg,#c88a1a,#ffe07a);', stBox);
    this.bossGuardLabel = el('div', GB_LABEL + 'right:0;top:' + BOSS_GB_TOP + 'px;', stWrap, '가드 브레이크');
    this.bossTag = el('div', 'display:none;font:900 13px ' + FONT + ';color:#1a2238;background:#ffd36a;border-radius:6px;padding:1px 10px;letter-spacing:2px;', this.bossBox, '무적');
    // 전환 알림 띠(위쪽 가장자리): 보스 이름 줄 자리에 잠깐 이름 대신 뜬다 — 보스 바 상자 크기가 늘지 않아 세로 화면에서도 목표 패널·자동 버튼과 겹치지 않는다.
    // 켜지고 꺼질 뿐 깜빡이지 않는다(섬광 규칙)
    this.bossRageBand = el('div', 'display:none;align-items:center;gap:8px;padding:0 12px;height:24px;box-sizing:border-box;border-radius:6px;white-space:nowrap;' +
      'background:linear-gradient(180deg,rgba(28,8,14,.92),rgba(10,4,12,.92));border:2px solid #ff4a1c;box-shadow:0 3px 10px rgba(0,0,0,.55);', nameRow);
    nameRow.insertBefore(this.bossRageBand, this.bossRageChip);
    this.bossRageBand.dataset.testid = 'boss-rage-band';
    this.bossRageBand.setAttribute('role', 'status');
    this.bossRageBand.setAttribute('aria-live', 'polite');
    this.bossRageBandHead = el('span', 'font:900 16px ' + FONT + ';letter-spacing:2px;color:#ffb040;' + TEXT_OUTLINE, this.bossRageBand, '');
    this.bossRageBandTip = el('span', 'font:800 13px ' + FONT + ';color:#f4ecd8;' + TEXT_OUTLINE, this.bossRageBand, '');
    {
      const qp = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
      this.rageBandMs = qp && qp.has('qa') && qp.has('bandhold') ? 600000 : RAGE_BAND_MS;
    }

    // 대상 프레임: 이름 + 체력 바 + 자세 바(보스 바와 같은 자리, 보스가 없을 때만)
    // 어두운 받침판: 뒤(VFX 층)의 큰 피해 숫자·이펙트가 비쳐도 이름·바가 읽히게 한다(COMBAT-FEEL 5.1)
    this.targetBox = el('div', 'position:absolute;left:50%;top:' + TARGET_TOP + 'px;transform-origin:50% 0;display:none;flex-direction:column;align-items:center;gap:3px;' +
      'padding:3px 12px ' + TARGET_PAD_BOTTOM + 'px;border-radius:8px;background:linear-gradient(180deg,rgba(8,12,26,.78),rgba(8,12,26,.55));', this.root);
    this.targetBox.dataset.testid = 'target-frame';
    this.targetName = el('div', 'font:900 16px ' + FONT + ';letter-spacing:2px;' + GOLD_TEXT + GOLD_SHADOW, this.targetBox, '');
    this.targetHp = makeBar(this.targetBox, TARGET_BAR_W, 16, '#c0202c', '#ff7a5a');
    this.targetHp.text.style.display = 'none';
    const tpWrap = el('div', 'position:relative;', this.targetBox);
    this.targetPostureBox = el('div', 'width:' + POSTURE_W + 'px;height:' + POSTURE_H + 'px;background:#0b1020;border:' + POSTURE_BORDER + 'px solid ' + C.gold + ';border-radius:3px;overflow:hidden;', tpWrap);
    this.targetPosture = el('div', 'height:100%;width:0%;background:' + POSTURE_GOLD + ';', this.targetPostureBox);
    // 가드 브레이크 글자는 자세 바 오른쪽(보스 경직 바와 같은 규칙)에 어두운 판을 깔고 둔다.
    // 바 아래에 두면 몹 머리 위 큰 피해 숫자(진홍 치명 배경)와 겹쳐 읽히지 않았다(COMBAT-FEEL 5.1)
    this.targetGuardLabel = el('div', GB_LABEL + 'font-size:15px;right:0;top:' + TARGET_GB_TOP + 'px;padding:1px 7px;border-radius:5px;background:rgba(10,6,14,.82);', tpWrap, '가드 브레이크');

    // 저스트 회피 글자: 상단 띠 안에서 섬광·흔들림 없이 잠깐 표시한다.
    this.justWrap = el('div', 'position:absolute;left:50%;top:64px;transform-origin:50% 0;pointer-events:none;', this.root);
    this.justText = el('div', 'opacity:0;white-space:nowrap;font:900 30px ' + FONT + ';letter-spacing:4px;color:#ffe7a0;' + TEXT_OUTLINE, this.justWrap, '저스트 회피');

    // 각성 게이지(하단 중앙)
    this.awakenBox = el('div', 'position:absolute;left:50%;bottom:14px;transform-origin:50% 100%;display:flex;flex-direction:column;align-items:center;gap:2px;', this.root);
    this.awakenLabel = el('div', 'font:900 14px ' + FONT + ';color:#fff;text-shadow:0 1px 3px #000;letter-spacing:2px;', this.awakenBox, '각성');
    const aBox = el('div', 'width:240px;height:12px;background:#0b1020;border:2px solid ' + C.gold + ';border-radius:8px;overflow:hidden;', this.awakenBox);
    this.awakenBar = aBox;
    this.awakenBox.dataset.testid = 'awaken-gauge';
    this.awakenFill = el('div', 'height:100%;width:0%;background:linear-gradient(90deg,#b3101f,#ff4a5a);', aBox);

    // PC 스킬 바: 각성 게이지를 바 위 자리로 옮겨 붙인다(배율은 스킬 바가 함께 맞춘다)
    if (!touch) {
      this.skillbar = new SkillBar(this.root, view, input, send);
      const s = this.awakenBox.style;
      s.position = 'relative';
      s.left = s.bottom = 'auto';
      s.transform = 'none';
      s.flexDirection = 'row';
      s.gap = '8px';
      this.awakenLabel.style.fontSize = '12px';
      this.skillbar.gaugeSlot.appendChild(this.awakenBox);
    }

    // 피격 가장자리(붉은 비네트, 흰 섬광이 아니다)
    this.hurt = el('div', 'position:absolute;inset:0;box-shadow:inset 0 0 140px 30px rgba(220,20,40,.7);opacity:0;', this.root);
    // 스킬 연계(LINK) 도장·속도선(linkHud.ts): 도장은 콤보 상자 아래 오른쪽 가장자리, 속도선은 HUD 맨 아래 층
    this.link = new LinkHud(this.root, view, touch);
    this.rampage = new RampageHud(this.root, view, touch);
    this.linkHint = new LinkHint(this.root, view, touch);

    // HUD 효과 Animation은 여기서 한 번만 만든다. 이벤트는 restart()로 되감아 재생만 한다(COMBAT-FEEL 5.1)
    this.bossGuardAnim = prepare(this.bossGuardLabel, GB_MOVE, 800, 'ease-out');
    this.targetGuardAnim = prepare(this.targetGuardLabel, GB_MOVE, 800, 'ease-out');
    this.justAnim = prepare(this.justText, JUST_MOVE, 600, 'ease-out');
    this.healAnim = prepare(this.healText, HEAL_MOVE, 900, 'ease-out');
    this.xpTickAnim = prepare(this.xpTick, XPTICK_MOVE, 900, 'ease-out');
    this.bandAnim = prepare(this.bandInner, BAND_MOVE, BAND_MS, 'ease-out');
    this.bandSweepAnim = prepare(this.bandSweep, BAND_SWEEP, 1000, 'ease-in-out');
    this.bandNoteAnim = prepare(this.bandNote, BAND_MOVE, BAND_MS, 'ease-out');
    this.bandHelpAnim = prepare(this.bandHelp, BAND_MOVE, 4500, 'ease-out');
    this.skillBandAnim = prepare(this.skillBand, SKILL_BAND_MOVE, SKILL_BAND_MS, 'ease-out');
    this.motionQuery = typeof matchMedia !== 'undefined' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.syncPrefs();
    // 예약 영역 크기: 박스가 실제로 바뀔 때만 브라우저가 알려 준다(프레임 루프에서 레이아웃을 조회하지 않는다)
    if (reserve && typeof ResizeObserver !== 'undefined') {
      const boxes: readonly HTMLElement[] = [this.bossBox, this.targetBox, this.bossGuardLabel, this.targetGuardLabel];
      const ro = new ResizeObserver((entries) => {
        for (const en of entries) {
          const i = boxes.indexOf(en.target as HTMLElement);
          if (i < 0) continue;
          const bs = en.borderBoxSize?.[0];
          this.obsW[i] = bs ? bs.inlineSize : en.contentRect.width;
          this.obsH[i] = bs ? bs.blockSize : en.contentRect.height;
        }
        this.reserveDirty = true;
      });
      for (const b of boxes) ro.observe(b, { box: 'border-box' });
    }
  }

  /** 오른쪽 위 묶음 배치(hudMenuLayout). 화면 크기·장면이 바뀔 때만 다시 놓는다 */
  private layoutTopRight(w: number, h: number, _u: number): void {
    const scene = combatScene(this.view.flow.scene) ? 'dungeon' : 'village';
    const more = this.menu.expanded;
    const compact = objectiveCompact(h, !!this.view.raid?.active);
    const key = w + 'x' + h + ',' + scene + (more ? ',more' : '') + (compact ? ',oc' : '');
    if (key === this.topRightKey) return;
    this.topRightKey = key;
    const p = hudMenuLayout(w, h, scene, { touch: this.touch, expanded: more });
    const o = p.bounds;
    const place = (element: HTMLElement, r: HudRect): void => {
      element.style.left = (r.x - o.x) + 'px'; element.style.top = (r.y - o.y) + 'px';
      element.style.width = r.w + 'px'; element.style.height = r.h + 'px';
    };
    this.tr.style.left = o.x + 'px'; this.tr.style.top = o.y + 'px';
    this.tr.style.width = o.w + 'px'; this.tr.style.height = o.h + 'px';
    this.tr.dataset.mode = p.mode; this.tr.dataset.more = more ? '1' : '0';
    place(this.minimapBox, p.minimap); place(this.stagePanel, p.plate); place(this.cameraBtn, p.camera);
    this.menu.place(p, o.x, o.y);
    const fs = Math.round(Math.max(11, Math.min(14, p.plate.h * 0.54)));
    this.stageFs = fs;
    this.stageText.style.fontSize = fs + 'px'; this.clockText.style.fontSize = fs + 'px';
    this.fitStageText();
    // 목표 카드(던전 왼쪽, tr 밖 형제 — 화면 좌표)
    const q = objectiveHudLayout(w, 0, 0, 0, compact);
    this.objText.style.display = compact ? 'none' : '';
    this.questTitle.style.whiteSpace = compact ? 'nowrap' : 'normal';
    this.questTitle.style.overflow = compact ? 'hidden' : '';
    this.questTitle.style.textOverflow = compact ? 'ellipsis' : '';
    this.questTitle.style.minWidth = compact ? '0' : '';
    this.quest.style.left = q.x + 'px'; this.quest.style.top = q.y + 'px'; this.quest.style.width = q.w + 'px'; this.quest.style.height = q.h + 'px';
    this.questTitle.parentElement!.style.flexDirection = w < 600 && !compact ? 'column' : 'row';
    this.questTitle.parentElement!.style.alignItems = w < 600 && !compact ? 'flex-start' : 'baseline';
  }

  /** 명판 글자 기준 크기(배치가 정한다) */
  private stageFs = 13;
  /** 긴 던전 이름이 명판에서 잘리지 않게 글자를 줄인다(최소 9px). 이름·배치가 바뀔 때만 잰다 */
  private fitStageText(): void {
    const t = this.stageText;
    let fs = this.stageFs;
    t.style.fontSize = fs + 'px';
    // 시계 그림은 지역 이름이 먼저 다 보이도록 좁을 때만 접는다(출시 게이트 RG-009: 인앱 390 "1-1 절벽 테라스 유적" 잘림)
    const icon = this.clockText.parentElement?.firstElementChild as HTMLElement | null | undefined;
    if (icon) icon.style.display = '';
    const over = (): boolean => t.clientWidth > 0 && t.scrollWidth > t.clientWidth + 1;
    while (fs > 9 && over()) { fs--; t.style.fontSize = fs + 'px'; }
    if (icon && over()) icon.style.display = 'none';
    this.stagePanel.title = this.stageText.textContent ?? '';
  }

  private layoutCombo(w: number, h: number, count: number): void {
    const key = w + 'x' + h + ',' + String(Math.max(0, count)).length;
    if (key === this.comboLayoutKey) return;
    this.comboLayoutKey = key;
    const p = comboHudLayout(w, h, count);
    this.comboBox.style.left = p.x + 'px'; this.comboBox.style.top = p.y + 'px';
    this.comboBox.style.width = p.w + 'px'; this.comboBox.style.height = p.h + 'px';
    this.comboRow.style.flexDirection = p.stacked ? 'column' : 'row';
    this.comboRow.style.alignItems = p.stacked ? 'flex-end' : 'baseline';
    this.comboRow.style.gap = p.stacked ? '0' : '6px';
    this.comboRow.style.height = p.rowHeight + 'px';
    this.comboNum.style.fontSize = p.numberFont + 'px';
    this.comboWord.style.fontSize = p.wordFont + 'px';
    this.praise.style.fontSize = p.praiseFont + 'px';
    // 스타일 랭크는 콤보 숫자·COMBO 글자 바로 오른쪽(콤보가 없어도 같은 자리)
    this.rampage.placeRank(p.x + p.w + 6, p.y + p.rowHeight / 2 - 42);
  }

  private onEvent = (e: GameEvent): void => {
    this.skillbar?.onEvent(e);
    this.link.onEvent(e);
    this.rampage.onEvent(e);
    this.linkHint.onEvent(e);
    if (e.type === Ev.ComboPraise) {
      const tier = PRAISE_TIERS[e.a - 1];
      if (!tier) return;
      this.praise.textContent = tier.label;
      this.praiseAnim?.cancel();
      this.praiseAnim = this.praise.animate(
        [
          { opacity: 1, transform: 'scale(1.08)', offset: 0 },
          { opacity: 1, transform: 'scale(1)', offset: 0.1 },
          { opacity: 1, transform: 'scale(1)', offset: 0.7 },
          { opacity: 0, transform: 'scale(1)', offset: 1 },
        ],
        { duration: 1200, easing: 'ease-out' },
      );
    } else if (e.type === Ev.HeroHurt) {
      this.hurtAnim?.cancel();
      this.hurtAnim = this.hurt.animate([{ opacity: 0.55 }, { opacity: 0 }], { duration: 260, easing: 'ease-out' });
      this.flashGate.ask(HUD_BAR_HERO, e.tick, this.view.tick);
    } else if (e.type === Ev.Hit && e.b === 1) {
      // 결투장식: 보스를 때린 순간 보스 체력 바가 하얗게 번쩍인다(화면 리미터를 거쳐 update 끝에 많아야 1회)
      this.flashGate.ask(HUD_BAR_BOSS, e.tick, this.view.tick);
    } else if (e.type === Ev.Hit && e.b === 0) {
      // 방패 고블린·엘리트를 때리면 대상 프레임이 뜨고 그 체력 바가 번쩍인다
      if (this.target.hit(this.view.mobs, e.c, this.nowMs)) this.flashGate.ask(HUD_BAR_TARGET, e.tick, this.view.tick);
    } else if (e.type === Ev.GuardBreak) {
      if (e.a === 1) restart(this.bossGuardAnim);
      else if (this.target.guardBreak(this.view.mobs, e.b, this.nowMs)) restart(this.targetGuardAnim);
    } else if (e.type === Ev.JustDodge) {
      // 색(섬광 약화면 회색 톤)은 syncPrefs가 설정이 바뀔 때만 쓴다
      restart(this.justAnim);
    } else if (e.type === Ev.HeroHeal) {
      if (!(e.a > 0)) return;
      const heal = Math.round(e.a);
      if (heal !== this.lastHeal) { this.lastHeal = heal; this.healText.textContent = '+' + fmtNum(heal); }
      restart(this.healAnim);
    } else if (e.type === Ev.RoomEnter) {
      // 지역 요령: 방에 처음 들어갈 때 한 번(같은 판의 중복 이벤트는 거른다, 재도전하면 다시)
      // 레이드 판은 지역 지도(r2)를 빌려 쓰지만 지역 요령은 맞지 않는다(레이드 요령은 지휘소 창과 레이드 HUD가 맡는다)
      const raid = !!this.view.raid && this.view.raid.active;
      const tip = raid ? null : this.tipGate.take(this.view.flow.mapId, e.a, this.view.flow.sceneSerial);
      if (tip) this.showTip(tip);
    } else if (e.type === Ev.XpGain) {
      if (e.a > 0) this.xpAcc += Math.round(e.a);
    } else if (e.type === Ev.LevelUp) {
      const parts = levelUpParts(Math.round(e.a), Math.max(1, Math.round(e.c)));
      this.bandHead.textContent = '레벨 업';
      this.bandTail.textContent = parts.tail;
      restart(this.bandAnim);
      const note = xpBandLevelUpNote(Math.round(e.a), Math.max(1, Math.round(e.c)));
      if (note) { this.bandNote.textContent = note; restart(this.bandNoteAnim); } else this.bandNoteAnim.cancel();
      if (this.reduceMotionOn !== 1) restart(this.bandSweepAnim);
    } else if (e.type === Ev.SkillLearned) {
      // 레벨업으로 새 스킬 자동 습득(D1e). 이 이벤트는 같은 묶음의 LevelUp 바로 뒤에 나므로 레벨업 띠 아래에 이어 보인다
      // 한 번의 EXP로 해금 레벨을 여럿 넘으면 같은 묶음에 여러 건이 온다: 앞 띠를 덮지 않게 대기열에 쌓고 update()가 차례로 보인다
      this.skillBandQueue.push(Math.round(e.a));
    } else if (e.type === Ev.SceneChange) {
      this.lastCombo = -1;
      this.xpAcc = 0;
      // 클리어 EXP로 오른 레벨업은 LevelUp 바로 뒤 같은 묶음에서 결과 장면으로 넘어간다: 띠는 결과·패배 화면 위에서 끝까지 보인다.
      // 새 던전·마을로 들어갈 때만 남은 띠를 지운다
      if (e.a !== Scene.Result && e.a !== Scene.Defeat) { this.bandAnim.cancel(); this.bandNoteAnim.cancel(); this.bandHelpAnim.cancel(); this.bandSweepAnim.cancel(); this.skillBandAnim.cancel(); this.skillBandQueue.clear(); }
      this.xpTickAnim.cancel();
      // 장면이 바뀌면 남은 칭찬 팝업·피격 비네트·글자 효과·바 번쩍임을 지우고 모아 둔 번쩍임 요청을 버린다
      this.praiseAnim?.cancel();
      this.hurtAnim?.cancel();
      this.comboAnim?.cancel();
      this.cancelTextFx();
      this.cancelBarFlash();
      this.flashGate.clear();
      this.target.reset();
      this.hideTip(true);
      // 모델만 지우면 패배·결과 화면에서는 update()가 일찍 끝나 프레임 DOM이 남는다. 여기서 바로 숨긴다.
      this.renderTarget(this.target.out);
      this.praiseAnim = this.hurtAnim = this.comboAnim = null;
      this.praise.style.opacity = '0';
      this.hurt.style.opacity = '0';
    }
  };

  /**
   * 요령 토스트 자리(roomTipLayout): 왼쪽 띠의 자동 버튼·자동 이동 알약 아래 → 머리글 없는 작은 모양 → 상단 HUD 띠 바로 아래 가운데 순서(PR #211 리뷰 P2).
   * 알약 자리는 AutoQuestUi.place()가 autoStatusHudLayout으로 정하고 높이는 글자 줄바꿈에 따르므로 DOM 사각형을 읽는다
   */
  private placeTip(): void {
    const W = viewW(), H = viewH(), obj = objectiveHudLayout(W);
    const st = this.autoStatusEl ?? (this.autoStatusEl = document.querySelector<HTMLElement>('[data-testid="auto-status"]'));
    const r = st && st.style.display !== 'none' ? viewRect(st) : null;
    const sr = r && r.width > 0 && r.height > 0 ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
    const sy = sr ? Math.round(sr.y) : -1, sh = sr ? Math.round(sr.h) : -1, sx = sr ? Math.round(sr.x) : -1;
    if (W === this.kTipW && H === this.kTipH && sx === this.kTipSx && sy === this.kTipSy && sh === this.kTipSh && this.tipBase === this.kTipBase && this.tipSerial === this.kTipSerial) return;
    this.kTipW = W; this.kTipH = H; this.kTipSx = sx; this.kTipSy = sy; this.kTipSh = sh; this.kTipBase = this.tipBase; this.kTipSerial = this.tipSerial;
    const s = this.tipBox.style;
    // 두 모양의 배율 전 크기를 잰다(offsetWidth·offsetHeight는 transform 배율 전)
    this.tipHead.style.display = 'none'; s.padding = '5px 10px 6px';
    const hC = this.tipBox.offsetHeight;
    this.tipHead.style.display = 'block'; s.padding = '9px 14px 10px';
    const hN = this.tipBox.offsetHeight, tw = this.tipBox.offsetWidth || 300;
    const L = roomTipLayout(W, H, obj.y + obj.h, sr, tw, hN, hC, this.tipBase);
    if (L.compact) { this.tipHead.style.display = 'none'; s.padding = '5px 10px 6px'; }
    s.left = L.x + 'px'; s.top = L.y + 'px';
    s.transform = 'scale(' + L.scale.toFixed(3) + ')';
    this.tipScale = L.scale;
  }

  private showTip(text: string): void {
    this.tipText.textContent = text;
    this.tipSerial++;
    this.tipUntil = this.nowMs + ROOM_TIP_MS;
    this.placeTip();
    const s = this.tipBox.style;
    s.transition = this.reduceMotionOn === 1 ? 'none' : 'opacity .25s';
    s.visibility = 'visible';
    s.opacity = '1';
  }

  private hideTip(instant: boolean): void {
    if (this.tipUntil === 0) return;
    this.tipUntil = 0;
    const s = this.tipBox.style;
    s.transition = instant || this.reduceMotionOn === 1 ? 'none' : 'opacity .25s,visibility 0s linear .25s';
    s.opacity = '0';
    s.visibility = 'hidden';
  }

  private cancelTextFx(): void {
    this.xpTickAnim.cancel();
    this.justAnim.cancel();
    this.healAnim.cancel();
    this.bossGuardAnim.cancel();
    this.targetGuardAnim.cancel();
  }

  private cancelBarFlash(): void {
    this.hpBar.flash.cancel();
    this.bossBar.flash.cancel();
    this.targetHp.flash.cancel();
  }

  /**
   * 섬광 약화(게임 설정)·동작 줄이기(OS)를 효과에 반영한다. 값이 바뀔 때만 키프레임을 바꾸고 진행 중인 효과를 취소한다(잔상 없음).
   * 매 update 호출: 바뀌지 않으면 비교 두 번뿐이다.
   */
  private syncPrefs(): void {
    const rf = this.view.settings.reduceFlash ? 1 : 0;
    if (rf !== this.reduceFlashOn) {
      this.reduceFlashOn = rf;
      this.cancelBarFlash();
      const frames = rf ? FLASH_CALM : FLASH_FULL;
      setFrames(this.hpBar.flash, frames);
      setFrames(this.bossBar.flash, frames);
      setFrames(this.targetHp.flash, frames);
      this.justText.style.color = rf ? '#c4c8d0' : '#ffe7a0';
    }
    const rm = this.motionQuery?.matches ? 1 : 0;
    if (rm !== this.reduceMotionOn) {
      this.reduceMotionOn = rm;
      this.cancelTextFx();
      setFrames(this.bossGuardAnim, rm ? GB_CALM : GB_MOVE);
      setFrames(this.targetGuardAnim, rm ? GB_CALM : GB_MOVE);
      setFrames(this.justAnim, rm ? JUST_CALM : JUST_MOVE);
      setFrames(this.healAnim, rm ? HEAL_CALM : HEAL_MOVE);
      setFrames(this.xpTickAnim, rm ? XPTICK_CALM : XPTICK_MOVE);
      setFrames(this.bandAnim, rm ? BAND_CALM : BAND_MOVE);
      setFrames(this.bandNoteAnim, rm ? BAND_CALM : BAND_MOVE);
      setFrames(this.bandHelpAnim, rm ? BAND_CALM : BAND_MOVE);
      setFrames(this.skillBandAnim, rm ? SKILL_BAND_CALM : SKILL_BAND_MOVE);
      this.bandSweepAnim.cancel();
      this.xpFill.style.transition = rm ? 'none' : 'width .35s ease-out';
    }
  }

  /** 레벨·EXP 막대와 '+N EXP' 알림. 값이 바뀔 때만 DOM을 쓴다(성장 정보가 없는 빌드에서는 아무것도 하지 않는다) */
  private syncLevel(): void {
    if (this.xpSnap) { this.xpSnap = false; this.xpFill.style.transition = this.reduceMotionOn === 1 ? 'none' : 'width .35s ease-out'; }
    const g = growthOf(this.view);
    const on = g ? 1 : 0;
    if (on !== this.lvShown) {
      this.lvShown = on;
      this.lvPlate.style.display = on ? 'flex' : 'none';
      this.xpBox.style.display = on ? 'block' : 'none';
      if (!on) { this.lastBandBadge = ''; this.xpBandBadge.style.display = 'none'; this.lastLv = -1; this.lastGRev = -1; }
    }
    if (!g) return;
    if (g.revision !== this.lastGRev || g.level !== this.lastLv) {
      this.lastGRev = g.revision;
      const h = treeHeader(g);
      const pct = Math.round(h.xpFrac * 1000) / 10;
      if (h.level !== this.lastLv) {
        // 레벨이 바뀌면 막대가 거꾸로 훑지 않게 이번 프레임만 전환을 끈다
        if (this.lastLv >= 0) { this.xpFill.style.transition = 'none'; this.xpSnap = true; }
        this.lastLv = h.level;
        this.lvText.textContent = 'Lv.' + h.level;
        const bt = xpBandBadgeText(h.level);
        const help = xpBandHelpText(h.level);
        this.xpBandBadge.title = help;
        this.bandHelp.textContent = help;
        if (bt !== this.lastBandBadge) {
          this.lastBandBadge = bt;
          this.xpBandBadge.textContent = bt;
          this.xpBandBadge.style.display = bt ? 'block' : 'none';
        }
      }
      if (pct !== this.lastXpPct) {
        this.lastXpPct = pct;
        this.xpFill.style.width = pct + '%';
        this.xpBox.setAttribute('aria-valuenow', String(Math.round(pct)));
        this.xpBox.title = h.xpText;
      }
    }
    const helpRun = this.bandHelpAnim.playState === 'running';
    if (helpRun !== this.helpOn) {
      this.helpOn = helpRun;
      const vis = helpRun ? 'hidden' : 'visible';
      this.goldLine.style.visibility = vis; this.xpBandBadge.style.visibility = vis;
    }
    if (this.xpAcc > 0 && this.nowMs - this.xpTickMs >= XP_TICK_GAP) {
      this.xpTick.textContent = '+' + fmtNum(this.xpAcc) + ' EXP';
      this.xpAcc = 0;
      this.xpTickMs = this.nowMs;
      restart(this.xpTickAnim);
    }
  }

  private hintSuppressed = false;
  /** 대균열 입구 선택 띠가 열린 동안은 연계 안내 줄을 접는다(같은 아래 가운데 자리를 덮는다) */
  suppressLinkHint(on: boolean): void { this.hintSuppressed = on; }

  /** u: 디자인 해상도 배율 */
  /** 터치 세로 배치: 각성 게이지를 버튼 묶음 위로 옮긴다(TouchControls.gaugeAnchor). PC 스킬 바가 있으면 무시 */
  anchorAwaken(a: { on: boolean; x: number; bottom: number; width: number }, u: number): void {
    if (this.skillbar) return;
    const key = a.on ? Math.round(a.x) + ',' + Math.round(a.bottom) + ',' + Math.round(a.width) + ',' + u.toFixed(3) : 'off';
    if (key === this.anchorKey) return;
    this.anchorKey = key;
    const s = this.awakenBox.style;
    if (a.on) { s.left = a.x + 'px'; s.bottom = a.bottom + 'px'; this.awakenBar.style.width = Math.min(240, a.width / u) + 'px'; }
    else { s.left = '50%'; s.bottom = '14px'; this.awakenBar.style.width = '240px'; }
  }

  /** 장착 단계가 바뀌면 초상을 바꾼다. 이전 단계 이미지 오류로 숨겨진 상태·대체 글자 K를 되돌리고 새 오류를 다시 받는다 */
  private setPortrait(tier: number): void {
    const im = this.portraitImg!, fb = this.portraitFallback;
    this.portraitTier = tier;
    // 라이라 초상 원화는 아직 없다(c022, 에셋 레인 몫): 카엘 초상 대신 청록 머리글자
    if (this.shownHero !== 'kael') { im.style.display = 'none'; if (fb) { fb.textContent = 'L'; fb.style.color = '#3fd0c0'; fb.style.display = 'block'; } return; }
    if (fb) { fb.textContent = 'K'; fb.style.color = '#b3202f'; }
    im.style.display = 'block';
    if (fb) fb.style.display = 'none';
    im.addEventListener('error', () => { im.style.display = 'none'; if (fb) fb.style.display = 'block'; }, { once: true });
    im.src = hudUrl(portraitImage(tier as 1 | 2 | 3));
    im.dataset.tier = String(tier);
  }

  update(u: number): void {
    const v = this.view;
    const sc = v.flow.scene;
    const live = sc === Scene.Village || combatScene(sc);
    this.root.style.display = live || sc === Scene.Result || sc === Scene.Defeat ? 'block' : 'none';
    // 오른쪽 위 메뉴·미니맵은 마을·던전에서만(결과·패배 화면에는 메뉴를 두지 않는다)
    if (live !== this.trOn) { this.trOn = live; this.tr.style.display = live ? 'block' : 'none'; }
    this.menu.update(live);
    // 렌더 벽시계는 update마다 한 번 읽는다(번쩍임 리미터·대상 프레임). 게임 틱은 이벤트 신선도에만 쓴다
    this.nowMs = performance.now();
    this.syncPrefs();
    // 대상 프레임의 몹 이름표는 지금 지역의 것(마을·R1은 기존 이름 그대로)
    this.target.names = mobNamesFor(v.flow.mapId);
    this.cursor = v.events.readFrom(this.cursor, this.onEvent);
    // 새 스킬 띠 대기열: 앞 띠 연출이 끝났을 때 다음 건을 보인다(결과·패배 화면에서도 끝까지 이어진다)
    if (this.skillBandQueue.count > 0) {
      // 같은 레벨업으로 얻은 새 스킬은 레벨 알림 다음에 표시해 두 정보를 모두 읽게 한다.
      const sk = this.skillBandQueue.take(this.skillBandAnim.playState === 'running' || this.bandAnim.playState === 'running');
      if (sk > 0) {
        const parts = skillLearnedParts(sk, slotsOf(this.view), this.view.heroId ?? 'kael');
        this.skillBandHead.textContent = '스킬 습득';
        this.skillBandTail.textContent = parts.tail;
        this.skillBand.setAttribute('aria-label', '스킬 습득 · ' + parts.tail);
        const hero = this.view.heroId ?? 'kael', fb = (hero === 'lyra' ? LYRA_ICONS : SKILL_ICONS)[SKILL_BTN_ALL[sk - 1]] ?? '';
        const jb: 0 | 1 = this.view.job?.branch === 1 ? 1 : 0;
        if (this.skillBandArt) this.skillBandArt.set(hero, sk, fb, jb); else this.skillBandArt = mountSkillArt(this.skillBandIcon, hero, sk, fb, jb);
        this.skillBandIcon.style.display = this.skillBandArt.root.dataset.state === 'absent' ? 'none' : 'block';
        restart(this.skillBandAnim);
      }
    }
    const skillNotice = this.skillBandAnim.playState === 'running';
    if (skillNotice !== this.bandCovered) { this.bandCovered = skillNotice; this.bandInner.style.visibility = skillNotice ? 'hidden' : 'visible'; }
    const noticeW = Math.min(470, (viewW() - 16) / Math.min(1, Math.max(.8, u)));
    if (noticeW !== this.bandWidth) { this.bandWidth = noticeW; this.bandOuter.style.width = noticeW + 'px'; }
    // 결과·패배에서도 목표를 동기화하고, 같은 값이면 DOM을 다시 쓰지 않는다.
    const obj = v.flow.objectiveLabel;
    if (obj !== this.lastObj) { this.lastObj = obj; this.objText.textContent = '◇ ' + obj; }
    const tier = v.stats.armorTier;
    const hid = v.heroId ?? 'kael';
    if (hid !== this.shownHero) {
      this.shownHero = hid;
      if (this.nameText) this.nameText.textContent = hid === 'lyra' ? '라이라' : '카엘';
      if (this.portraitImg) this.setPortrait(tier);
    } else if (tier !== this.portraitTier && this.portraitImg) this.setPortrait(tier);
    // 환생 회차 별(이름표 옆, 1차 청금 ★ · 2차 진홍 ★★)
    const cyc = v.rebirth ? v.rebirth.cycle : 0;
    if (cyc !== this.shownCycle && this.starText) {
      this.shownCycle = cyc;
      const look = rebirthLook(cyc);
      this.starText.textContent = rebirthStars(cyc);
      this.starText.style.color = look.auraCss || '#fff';
      this.starText.style.display = cyc > 0 ? 'inline' : 'none';
    }
    // 마을에서는 왼쪽 목표 알림판 대신 퀘스트 카드(villageQuest.ts)가 같은 자리에 선다
    const questOn = sc !== Scene.Village;
    if (questOn !== this.questOn) { this.questOn = questOn; this.quest.style.display = questOn ? 'block' : 'none'; }
    if (!live) {
      // 전투 밖에서는 번쩍임을 내지 않고 요청도 다음 장면으로 넘기지 않는다
      this.flashGate.clear();
      this.clearReserve();
      this.link.update(u, false);
      this.rampage.update(u, false);
      this.linkHint.update(false);
      this.hideTip(true);
      // 결과·패배 화면에도 HUD가 남는다. 쓰러진 타격과 장면 전환이 같은 틱이라 마지막 체력을 여기서 반영하고 체력 낮음 경고를 끈다.
      if (sc === Scene.Result || sc === Scene.Defeat) this.settle();
      return;
    }
    this.frame++;
    if (this.tipUntil > 0 && this.nowMs >= this.tipUntil) this.hideTip(false);
    // 요령이 떠 있는 동안 자동 이동 알약이 나타나거나 줄바꿈으로 커질 수 있어 몇 프레임마다 자리를 다시 잰다
    if (this.tipUntil > 0 && this.frame % 6 === 0) this.placeTip();
    this.syncLevel();
    this.layoutTopRight(viewW(), viewH(), u);

    const tlScale = topLeftHudScale(viewW(), u);
    if (tlScale !== this.lastTopLeftScale) {
      this.lastTopLeftScale = tlScale;
      this.tl.style.transform = 'scale(' + tlScale.toFixed(3) + ')';
    }
    if (u !== this.lastScale) {
      this.lastScale = u;
      const s = 'scale(' + u.toFixed(3) + ')';
      this.bossBox.style.transform = 'translateX(-50%) ' + s;
      this.targetBox.style.transform = 'translateX(-50%) ' + s;
      this.bandOuter.style.transform = 'translateX(-50%) scale(' + Math.min(1, Math.max(.8, u)).toFixed(3) + ')';
      this.targetTopPx = Math.max(TARGET_TOP_MIN, Math.round(TARGET_TOP * u));
      this.targetBox.style.top = this.targetTopPx + 'px';
      this.justWrap.style.transform = 'translateX(-50%) ' + s;
      // 요령 토스트 글자가 너무 작아지지 않게 배율 하한 0.75(자리·배율 적용은 placeTip)
      this.tipBase = Math.max(u, 0.75);
      this.placeTip();
      if (!this.skillbar) this.awakenBox.style.transform = 'translateX(-50%) ' + s;
    }

    const h = v.hero;
    setBar(this.hpBar, h.hp, h.maxHp);
    setBar(this.mpBar, h.mp, h.maxMp);
    // 골드 한 줄(마을·던전·결과): 타이틀에서는 숨긴다
    const gt = goldText(v.profile);
    if (gt !== this.lastGold) { this.lastGold = gt; this.goldText.textContent = gt; this.goldLine.style.display = gt ? 'flex' : 'none'; }
    const dungeon = combatScene(sc);
    let status = (h.chillTicks > 0 ? 1 : 0) | (h.poisonTicks > 0 ? 2 : 0);
    // 비약 남은 시간(D2c): 초가 바뀔 때만 글자를 쓴다(미리 만든 mm:ss 표)
    const gv = v.gear;
    if (gv && gv.elix) {
      for (let k = 0; k < this.elixirChips.length; k++) {
        const left = Math.max(0, Math.min(ELIXIR_MAX_SEC, (gv.elix[k] ?? 0) - gv.play));
        if (left > 0) status |= 4 << k;
        if (left !== this.elixirLast[k]) { this.elixirLast[k] = left; this.elixirTime[k].textContent = TIME_TEXT[left]; }
      }
    }
    // 마을에서는 체력 바 아래가 퀘스트 카드에 가려지므로 표지 줄을 체력 바 오른쪽 끝으로 옮긴다(위쪽 가장자리, D2c 화면 확인)
    const vil = sc === Scene.Village;
    if (vil !== this.kStatusVillage) {
      this.kStatusVillage = vil;
      void vil; // HUD 정리: 마을·던전 같은 자리(골드 줄 오른쪽)
      this.statusBox.style.top = this.statusTop;
    }
    if (status !== this.kStatus) {
      this.kStatus = status;
      this.statusBox.style.display = status ? 'flex' : 'none';
      this.chillChip.style.display = status & 1 ? 'inline-block' : 'none';
      this.poisonChip.style.display = status & 2 ? 'inline-block' : 'none';
      // C2: 같은 규칙의 지역판 이름(모래 = 발 묶임, 심연 = 침식). 상태가 바뀔 때만 글자를 쓴다
      const th = v.map.hazardTheme;
      this.chillChip.textContent = th === 'sand' ? '발 묶임' : '둔화';
      this.poisonChip.textContent = th === 'void' ? '침식' : '중독';
      for (let k = 0; k < this.elixirChips.length; k++) this.elixirChips[k].style.display = status & (4 << k) ? 'inline-flex' : 'none';
    }
    this.skillbar?.update(u, dungeon);
    this.link.update(u, dungeon && this.bandAnim.playState !== 'running' && !skillNotice);
    this.rampage.update(u, dungeon);
    // 레벨업 띠가 내려와 있는 동안은 같은 자리(위쪽 가운데)의 연계 안내 줄을 접는다
    this.linkHint.update(dungeon && !this.hintSuppressed && (v.link?.stage ?? 0) < 2 && this.bandAnim.playState !== 'running' && this.skillBandAnim.playState !== 'running');
    // 연계 안내 줄이 보일 때만 알약·요령을 피해 자리를 다시 잡는다(프레임 루프에서 새 객체 없음, LinkHint.place)
    // 레이드 장면은 연계 띠를 가운데 아래에서 왼쪽 위 빈 줄로 옮긴다(raidHudLayout.raidHintRect)
    this.linkHint.setRaid(dungeon && !!v.raid?.active);
    if (this.linkHint.inRaid) {
      if (this.linkHint.visible && (this.frame % 6 === 0 || raidLayoutRev.n !== this.raidRev)) {
        this.raidRev = raidLayoutRev.n;
        // 사각형은 Hud 필드(rv*)에 값만 갱신한다(프레임 루프에서 새 객체를 만들지 않는다)
        const t = viewRect(this.tl, this.rvTl), g = this.goldLine.style.display === 'none' ? null : viewRect(this.goldLine, this.rvGold);
        const tl = this.raidTl;
        tl.x = t.left; tl.y = t.top; tl.w = t.width; tl.h = Math.max(t.height, g ? g.bottom - t.top : 0);
        // 피할 것: 보스 체력 줄, 한 줄 모양으로 올라온 파티 줄(raid-hud). 가로로 겹치는 것만 raidHintRect가 비킨다
        const obs = this.raidObs;
        const rb = this.bossBox.offsetWidth ? viewRect(this.bossBox, this.rvBoss) : null;
        const rh = this.raidHudEl ?? (this.raidHudEl = document.querySelector<HTMLElement>('[data-testid="raid-hud"]'));
        const rr = rh && rh.offsetWidth ? viewRect(rh, this.rvParty) : null;
        obs[0].x = rb ? rb.left : 0; obs[0].y = rb ? rb.top : 0; obs[0].w = rb ? rb.width : 0; obs[0].h = rb ? rb.height : 0;
        obs[1].x = rr ? rr.left : 0; obs[1].y = rr ? rr.top : 0; obs[1].w = rr ? rr.width : 0; obs[1].h = rr ? rr.height : 0;
        // 영웅 패널 아래 빈 줄이 모자라면(1280·1920) 파티 창 아래 왼쪽 띠 맨 아래로
        const below = rr && rr.height > 40 ? this.raidBelow : null;
        if (below && rr) { below.top = rr.bottom + 6; below.limit = viewH() - 8; }
        this.linkHint.placeRaid(tl, viewRect(this.quest, this.rvQuest).top, obs, below);
      }
    } else if (this.linkHint.visible && this.frame % 6 === 0) {
      this.linkHint.place(this.autoStatusEl ?? (this.autoStatusEl = document.querySelector<HTMLElement>('[data-testid="auto-status"]')), this.tipUntil > 0 ? this.tipBox : null, this.tipScale);
    }

    // 체력 낮음 경고: 살아 있고 던전 전투 중일 때만
    const ratio = h.maxHp > 0 ? h.hp / h.maxHp : 1;
    const low = dungeon && h.hp > 0 ? (ratio <= CRIT_HP ? 2 : ratio <= LOW_HP ? 1 : 0) : 0;
    if (low !== this.lastLow) {
      this.lastLow = low;
      this.lowHp.style.display = low ? 'block' : 'none';
      this.lowHp.style.animation = low === 2 ? CRIT_ANIM : low === 1 ? LOW_ANIM : 'none';
    }
    if (v.flow.stageLabel !== this.lastStage) {
      this.lastStage = v.flow.stageLabel;
      this.stageText.textContent = v.flow.stageLabel;
      this.fitStageText();
      this.questTitle.textContent = v.flow.stageLabel;
    }
    // 시련 판 진행 띠: 시련 rev가 바뀔 때만 DOM을 쓴다(속공은 1초, 무혈·연격은 값이 바뀔 때)
    const tv = v.trials, trialOn = !!tv && tv.live.id !== '' && sc !== Scene.Village;
    const trialKey = trialOn ? tv!.rev : -1;
    if (trialKey !== this.kTrial) {
      this.kTrial = trialKey;
      if (trialOn) {
        this.trialText.textContent = '⚔ ' + tv!.live.status;
        this.trialText.style.color = tv!.live.danger ? '#ff8a8a' : '#ffe7a8';
        this.trialText.style.display = 'block';
      } else this.trialText.style.display = 'none';
    }
    const cnt = dungeon && v.flow.objectiveTotal > 0 ? v.flow.objectiveKilled + ' / ' + v.flow.objectiveTotal : '';
    if (this.cntText.textContent !== cnt) this.cntText.textContent = cnt;
    if (dungeon) {
      const clock = v.flow.elapsedTicks;
      const sec = Math.floor(clock / 60);
      if (sec !== this.lastClock) { this.lastClock = sec; this.lastMinute = -1; this.clockText.textContent = fmtTime(clock); }
    } else {
      // 마을: 온라인 게임처럼 지금 시각(분이 바뀔 때만 문자열을 만든다)
      const now = Date.now(), min = Math.floor(now / 60000);
      if (min !== this.lastMinute) {
        this.lastMinute = min; this.lastClock = -1;
        const d = new Date(now);
        this.clockText.textContent = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
      }
    }

    // 콤보
    const cc = v.combo.count;
    this.layoutCombo(viewW(), viewH(), cc);
    this.comboBox.style.display = dungeon && cc > 0 ? 'block' : 'none';
    if (cc !== this.lastCombo) {
      if (cc > 0) {
        this.comboNum.textContent = String(cc);
        this.comboAnim?.cancel();
        this.comboAnim = this.comboNum.animate([{ transform: 'scale(1.18)' }, { transform: 'scale(1)' }], { duration: 110, easing: 'ease-out' });
      }
      this.lastCombo = cc;
    }
    if (cc > 0) {
      // 초기화 임박 시 흐려진다
      const left = v.combo.timer;
      const op = left < 40 ? Math.round((0.45 + (left / 40) * 0.55) * 20) : 20;
      if (op !== this.lastComboOp) { this.lastComboOp = op; this.comboBox.style.opacity = OPACITY[op]; }
    }

    // 보스
    const b = v.boss;
    const bossOn = dungeon && b.state !== BossState.Inactive && b.state !== BossState.Dead;
    this.bossBox.style.display = bossOn ? 'flex' : 'none';
    if (bossOn) {
      if (this.bossName.textContent !== b.name) this.bossName.textContent = b.name;
      setBar(this.bossBar, b.hp, b.maxHp);
      const invul = b.invulnerable === true;
      if (invul !== this.lastInvul) {
        this.lastInvul = invul;
        // 무적 태그 높이 변경은 다음 관측을 기다리지 않고 초기 영역으로 보호한다.
        this.obsH[OBS_BOSS] = 0; this.reserveDirty = true;
        this.bossTag.style.display = invul ? 'block' : 'none';
        this.bossBar.fill.style.filter = invul ? 'grayscale(.7) brightness(1.2)' : '';
      }
      this.syncBossRage(b.rageAt ?? 0, b.rage ?? 0, b.variant);
      const sg = Number.isFinite(b.stagger) ? Math.round(Math.max(0, Math.min(1, b.stagger)) * 100) : 0;
      if (sg !== this.lastStagger) {
        this.lastStagger = sg;
        this.staggerFill.style.width = sg + '%';
      }
    }

    // 대상 프레임(던전에서 보스가 없을 때만)
    if (dungeon) this.renderTarget(this.target.frame(this.nowMs, v.mobs, bossOn));
    else { this.target.reset(); this.renderTarget(this.target.out); }
    this.syncReserve(u, bossOn, this.lastTargetOn);
    // 바 번쩍임: 이번 update에서 모은 요청 중 보이는 바 하나를 화면 리미터가 허락할 때만(각성 연출 중에는 내지 않는다)
    const shown = HUD_BAR_HERO | (bossOn ? HUD_BAR_BOSS : 0) | (this.lastTargetOn ? HUD_BAR_TARGET : 0);
    const fb = this.flashGate.take(this.nowMs, shown, v.awaken.phase === AwakenPhase.None);
    if (fb !== 0) restart(fb === HUD_BAR_HERO ? this.hpBar.flash : fb === HUD_BAR_BOSS ? this.bossBar.flash : this.targetHp.flash);

    // 각성 게이지
    const g = Math.round(h.awakenGauge * 100);
    if (g !== this.lastAwaken) {
      this.lastAwaken = g;
      this.awakenFill.style.width = g + '%';
      this.awakenLabel.textContent = g >= 100 ? '각성 준비!' : '각성 ' + g + '%';
      this.awakenLabel.style.color = g >= 100 ? C.gold : '#fff';
    }
    this.awakenBox.style.display = dungeon ? 'flex' : 'none';
    // 레이드 장면(PC 스킬 바): 각성 게이지를 스킬 바 위 가운데(영웅·용병 발밑)에서 오른쪽 끝(각성 버튼 위)으로 옮기고 짧게 한다(2026-10-30 L11).
    // 스킬 바 뿌리는 left:50% 절대 배치라 너비가 화면 절반으로 묶여 align-self로는 못 옮긴다: 줄 너비(row)와 게이지 너비로 이동량을 잰다. 터치는 버튼 묶음 위 자리(anchorAwaken)가 이미 가장자리다
    if (this.skillbar) {
      const ar = dungeon && !!v.raid?.active;
      if (ar !== this.awakenRaid) {
        this.awakenRaid = ar;
        this.awakenShift = -1;
        this.awakenBar.style.width = ar ? '96px' : '240px';
        if (!ar) this.skillbar.gaugeSlot.style.transform = '';
      }
      if (ar && (this.frame & 7) === 0) {
        const row = this.skillbar.gaugeSlot.nextElementSibling as HTMLElement | null;
        const shift = row ? Math.max(0, Math.round((row.offsetWidth - this.awakenBox.offsetWidth) / 2 - 6)) : 0;
        if (shift !== this.awakenShift) { this.awakenShift = shift; this.skillbar.gaugeSlot.style.transform = 'translateX(' + shift + 'px)'; }
      }
    }
    // 각성 연출(모으기~복귀) 동안은 비워진 게이지 막대를 숨긴다(컷인 위에 빈 회색 막대가 남았다, VFX/룩 dream-loop 지적). 자리는 유지한다
    const awVis = v.awaken.phase === AwakenPhase.None ? 'visible' : 'hidden';
    if (this.awakenBox.style.visibility !== awVis) this.awakenBox.style.visibility = awVis;

    if ((this.frame & 3) === 0) this.drawMinimap();
  }

  /**
   * 대상 프레임·보스 바가 차지한 화면 영역을 예약 영역에 쓴다. 보임·배율·화면 폭·관측 크기가 바뀔 때만 다시 계산한다.
   * 두 박스는 left:50% + translateX(-50%) scale(u), 원점 50% 0이라 가운데 x와 top이 고정이고 크기만 u배가 된다.
   * 오른쪽 '가드 브레이크' 글자는 중심 원점 최대 확대 외곽까지 포함한다. 무적 태그가 켜지면 아래쪽은 보수적으로 더 확보한다.
   */
  private syncReserve(u: number, bossOn: boolean, targetOn: boolean): void {
    const r = this.reserve;
    if (!r) return;
    const vw = viewW();
    if (!this.reserveDirty && vw === this.reserveVw && u === this.reserveU && bossOn === this.reserveBoss && targetOn === this.reserveTarget) return;
    this.reserveDirty = false; this.reserveVw = vw; this.reserveU = u; this.reserveBoss = bossOn; this.reserveTarget = targetOn;
    const cx = vw / 2, w = this.obsW, h = this.obsH;
    // border-box(바의 테두리 포함). 이름·태그가 바뀐 프레임에도 최소 영역을 유지한다.
    if (bossOn) {
      const bw = Math.max(w[OBS_BOSS], BOSS_BAR_W + 4);
      const bh = h[OBS_BOSS] || BOSS_RESERVE_H;
      const gw = w[OBS_BOSS_GB] || GB_RESERVE_W;
      const gh = h[OBS_BOSS_GB] || GB_RESERVE_H;
      // 중심 원점 확대: 오른쪽 끝은 원래 왼쪽 + 폭*(1+scale)/2.
      const right = Math.max(bw / 2, (STAGGER_W + 2 * POSTURE_BORDER) / 2 + BOSS_GB_GAP + gw * (1 + GB_SCALE) / 2);
      const bottom = bh + Math.max(RESERVE_PAD, -POSTURE_H - 2 * POSTURE_BORDER + BOSS_GB_TOP + gh * (1 + GB_SCALE) / 2);
      r.set(RESERVE_BOSS, cx - (bw / 2 + RESERVE_PAD) * u, BOSS_TOP - RESERVE_PAD * u,
        cx + (right + RESERVE_PAD) * u, BOSS_TOP + bottom * u);
    } else r.clear(RESERVE_BOSS);
    if (targetOn) {
      const bw = Math.max(w[OBS_TARGET], TARGET_BAR_W + 4 + 24);
      const bh = h[OBS_TARGET] || TARGET_RESERVE_H;
      const gw = w[OBS_TARGET_GB] || GB_RESERVE_W;
      const gh = h[OBS_TARGET_GB] || GB_RESERVE_H;
      const right = Math.max(bw / 2, (POSTURE_W + 2 * POSTURE_BORDER) / 2 + TARGET_GB_GAP + gw * (1 + GB_SCALE) / 2);
      const bottom = bh + Math.max(RESERVE_PAD, -TARGET_PAD_BOTTOM - POSTURE_H - 2 * POSTURE_BORDER + TARGET_GB_TOP + gh * (1 + GB_SCALE) / 2);
      const top = this.targetTopPx;
      r.set(RESERVE_TARGET, cx - (bw / 2 + RESERVE_PAD) * u, top - RESERVE_PAD * u,
        cx + (right + RESERVE_PAD) * u, top + bottom * u);
    } else r.clear(RESERVE_TARGET);
  }

  /**
   * S5 분노·광폭 표시: 문턱·변형이 바뀔 때만 구간을 다시 그리고, 단계가 바뀌면 이름 옆 표와 알림 띠를 켠다(프레임마다 문자열·객체를 만들지 않는다).
   * 띠는 RAGE_BAND_MS 동안만, 표는 보스전 끝까지.
   */
  private syncBossRage(rageAt: number, rage: number, variant: number): void {
    if (rageAt !== this.lastRageAt || variant !== this.lastRageVariant) {
      this.lastRageAt = rageAt; this.lastRageVariant = variant;
      const def = BOSS_DEFS[BOSS_KEYS[variant] ?? 'treant'];
      const m = rageMark(def.tier, rageAt);
      const z = this.bossRageZone;
      if (!m) z.style.display = 'none';
      else {
        z.style.display = 'block';
        z.style.width = m.pct + '%';
        z.style.borderRightColor = m.edge;
        z.style.background = 'repeating-linear-gradient(90deg,' + m.color + '55 0 6px,' + m.color + '22 6px 12px)';
        this.bossRageZoneText.textContent = m.name + ' ' + Math.round(m.pct) + '%';
        z.title = RAGE_TEXT[m.kind].card;
      }
      this.lastRage = -1;
    }
    if (rage !== this.lastRage) {
      const first = this.lastRage === 0 && rage > 0;
      this.lastRage = rage;
      const k = rage === 1 || rage === 2 ? rage : 0;
      if (k === 0) {
        this.bossRageChip.style.display = 'none';
        this.bossRageBand.style.display = 'none';
        this.bossName.style.display = '';
        this.rageBandUntil = 0;
      } else {
        const t = RAGE_TEXT[k], col = k === 1 ? '#ff4a1c' : '#a040ff', edge = k === 1 ? '#ffb040' : '#ff5ae0';
        this.bossRageChip.textContent = t.name;
        this.bossRageChip.style.background = 'linear-gradient(' + edge + ',' + col + ')';
        this.bossRageChip.style.display = 'inline-block';
        // 띠는 전환을 실제로 본 경우만(HUD가 이미 분노 상태인 보스에 붙은 경우는 표만)
        if (first) {
          this.bossRageBandHead.textContent = t.head;
          this.bossRageBandHead.style.color = edge;
          this.bossRageBandTip.textContent = t.tip;
          this.bossRageBand.style.borderColor = col;
          this.bossRageBand.style.display = 'flex';
          this.bossName.style.display = 'none';
          this.rageBandUntil = this.nowMs + this.rageBandMs;
        }
        this.bossRageZone.style.background = 'repeating-linear-gradient(90deg,' + col + 'aa 0 6px,' + col + '55 6px 12px)';
      }
      this.obsH[OBS_BOSS] = 0; this.reserveDirty = true;
    }
    if (this.rageBandUntil > 0 && this.nowMs >= this.rageBandUntil) {
      this.rageBandUntil = 0;
      this.bossRageBand.style.display = 'none';
      this.bossName.style.display = '';
      this.obsH[OBS_BOSS] = 0; this.reserveDirty = true;
    }
  }

  private clearReserve(): void {
    if (!this.reserve) return;
    this.reserve.clear(RESERVE_BOSS); this.reserve.clear(RESERVE_TARGET);
    this.reserveBoss = this.reserveTarget = false;
  }

  /** 결과·패배 화면: 체력·MP 바를 최종 값으로 맞추고 체력 낮음 경고를 끈다(값이 같으면 DOM을 쓰지 않는다) */
  private settle(): void {
    const h = this.view.hero;
    setBar(this.hpBar, h.hp, h.maxHp);
    setBar(this.mpBar, h.mp, h.maxMp);
    if (this.lastLow !== 0) {
      this.lastLow = 0;
      this.lowHp.style.display = 'none';
      this.lowHp.style.animation = 'none';
    }
  }

  /** 값이 바뀔 때만 DOM을 쓴다 */
  private renderTarget(o: TargetFrameOut): void {
    if (o.visible !== this.lastTargetOn) {
      this.lastTargetOn = o.visible;
      this.targetBox.style.display = o.visible ? 'flex' : 'none';
    }
    if (!o.visible) return;
    const op = Math.round(o.opacity * 20);
    if (op !== this.lastTargetOp) { this.lastTargetOp = op; this.targetBox.style.opacity = OPACITY[op]; }
    if (o.name !== this.lastTargetName) { this.lastTargetName = o.name; this.targetName.textContent = o.name; }
    setBar(this.targetHp, o.hpRatio * 1000, 1000);
    const pp = Math.round(o.posture * 100);
    if (pp !== this.lastTargetPosture) { this.lastTargetPosture = pp; this.targetPosture.style.width = pp + '%'; }
    if (o.breaking !== this.lastTargetBreak) {
      this.lastTargetBreak = o.breaking;
      this.targetPosture.style.background = o.breaking ? POSTURE_BREAK : POSTURE_GOLD;
      this.targetPostureBox.style.borderColor = o.breaking ? C.crimson : C.gold;
    }
  }

  private drawMinimap(): void {
    const v = this.view;
    const g = v.grid;
    const ctx = this.mmCtx;
    const px = MM / (MM_R * 2);
    const hx = v.hero.x, hz = v.hero.z;
    ctx.fillStyle = '#0c1322';
    ctx.fillRect(0, 0, MM, MM);
    const cx0 = Math.floor(hx - g.originX) - MM_R;
    const cz0 = Math.floor(hz - g.originZ) - MM_R;
    const offX = (hx - g.originX) - Math.floor(hx - g.originX);
    const offZ = (hz - g.originZ) - Math.floor(hz - g.originZ);
    for (let j = 0; j < MM_R * 2 + 1; j++) {
      const cz = cz0 + j;
      if (cz < 0 || cz >= g.height) continue;
      for (let i = 0; i < MM_R * 2 + 1; i++) {
        const cx = cx0 + i;
        if (cx < 0 || cx >= g.width) continue;
        const idx = cz * g.width + cx;
        const cell = g.cells[idx];
        if (cell === Cell.Void) continue;
        const gate = g.gateCells[idx];
        if (cell === Cell.Solid) ctx.fillStyle = '#6c6552';
        else if (gate > 0 && v.flow.gateOpen[gate - 1] < 0.99) ctx.fillStyle = '#e08a2a';
        else ctx.fillStyle = '#3d4d6a';
        ctx.fillRect((i - offX) * px, (j - offZ) * px, px + 0.6, px + 0.6);
      }
    }
    const m = v.mobs;
    ctx.fillStyle = '#ff4a4a';
    for (let i = 0; i < m.capacity; i++) {
      if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) continue;
      const dx = m.x[i] - hx, dz = m.z[i] - hz;
      if (Math.abs(dx) > MM_R || Math.abs(dz) > MM_R) continue;
      ctx.fillRect(MM / 2 + dx * px - 1.5, MM / 2 + dz * px - 1.5, 3, 3);
    }
    const b = v.boss;
    if (b.state !== BossState.Inactive && b.state !== BossState.Dead) {
      const dx = b.x - hx, dz = b.z - hz;
      if (Math.abs(dx) < MM_R + 3 && Math.abs(dz) < MM_R + 3) {
        ctx.fillStyle = '#ff2a3d';
        ctx.beginPath();
        ctx.arc(MM / 2 + dx * px, MM / 2 + dz * px, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
    // 영웅: 바라보는 방향 삼각형
    ctx.save();
    ctx.translate(MM / 2, MM / 2);
    ctx.rotate(-v.hero.facing + Math.PI);
    ctx.fillStyle = '#ffd24a';
    ctx.beginPath();
    ctx.moveTo(0, -7);
    ctx.lineTo(5, 5);
    ctx.lineTo(-5, 5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}
